/**
 * Datasets tab — whole-video selection for delivery to a lab.
 *
 * Deliberately simpler than the Clips tab. Videos are all-or-nothing: there is no
 * density dial, no max-gap slider, no clip windows, and nothing here calls
 * buildWindows. A lab buying 1,000 hours wants whole recordings with a manifest,
 * not 19,000 trimmed mp4s.
 *
 * What it keeps from Clips: the time window, the task vocabulary and the industry
 * filter. What it adds: an organization filter, CLIP visual search alongside
 * description search, per-video capture metadata, and delivery into a bucket.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import {
  ACTIVITY_HIT_LIMIT,
  SEARCH_HIT_LIMIT,
  Bucket,
  BucketBreakdown,
  ExternalBucket,
  CART_KEY,
  CartItem,
  DeliveryJob,
  EnvCoverage,
  EnvLabel,
  DeepScanJob,
  ManifestRow,
  SearchHit,
  TOP_ACTIVITIES,
  Totals,
  VideosResponse,
  VisualHit,
  WHVideo,
  fmtBytes,
  fmtHours,
  getDeepScan,
  runDescriptionSearch,
  runDescriptionUnion,
  runVisualSearch,
  startDeepScan,
} from './whShared';
import {
  BalanceGroup,
  BalanceResult,
  balanceDualSelection,
  balanceSelection,
  donorAllowance,
} from './dsBalance';
import './DatasetPage.css';

// Defaults for THIS tab only -- the Clips tab keeps its own, which are tuned for
// reviewing hand activity rather than assembling a delivery. The state underneath
// is an EXCLUDE list so an org or industry nobody has seen yet is kept rather than
// silently hidden.
//
// Only our own footage is off by default. Everything else -- including night/IR --
// starts included, because a delivery is assembled by narrowing from the whole
// corpus, not by remembering what to switch back on.
const DEFAULT_OFF_INDS = ['Internal-Plix', 'Internal-PS'];
// Matched on org NAME (case- and space-insensitive: the org is stored as
// "DieboldNixdorf", not "Diebold Nixdorf"), resolved to ids once the first
// payload names them.
// Matched on normOrg (lowercased, separators stripped), so spelling variants of
// the same name collapse. A name with no footage yet is kept anyway -- it costs
// nothing and holds once that org starts appearing.
const DEFAULT_OFF_ORG_NAMES = ['plix', 'Plix-sales', 'Diebold Nixdorf',
  'Accurate Adjustments', 'laz', 'IHTechLabs'];
// Tasks a lab is never buying: phone use and driving dominate the tag counts
// without being deliverable "hand activity". Off by default, one click back on.
const DEFAULT_OFF_CATS = ['Driving', 'Using a phone'];
// How many of the donor's lost categories a backfill searches at a time. It
// widens by this much whenever the current set cannot close the gap.
const BACKFILL_TERMS = 5;
// The filter chips survive a reload. They are a curation decision, often an
// expensive one to rediscover: reopening the tab used to silently restore the
// defaults, and a chip worth 208h being back on reads as "the corpus is empty"
// rather than "a filter is on".
const FILTER_KEY = 'ds-filters-v1';
const savedFilters = (() => {
  try {
    return JSON.parse(localStorage.getItem(FILTER_KEY) || 'null') || null;
  } catch {
    return null;
  }
})();
const normOrg = (n: string) => n.toLowerCase().replace(/[\s_-]+/g, '');
const POLL_MS = 2000;
// This tab always reads the whole retained corpus. A window selector implied the
// choice mattered here; it never did -- you narrow a delivery by task, industry,
// org and search, not by recency.
// Seconds of lead-in before a match, so the player approaches the moment rather
// than landing in the middle of it. Same value plix-soc's MarkerControlPanel uses.
const PRE_START = 10;

/** One point in a video where the search matched: a description hit's timestamp,
 *  or a coalesced frame time from a visual hit. */
interface Marker {
  t: number;
  label: string;
}

type SearchMode = 'desc' | 'visual';

// Sentinel for the customer dropdown's "new customer" row. Not a customer name:
// customers are stored lowercased-trimmed, so a real "__new__" cannot collide.
const NEW_CUSTOMER = '__new__';
// How much more than the target a scan should bank before stopping. Its hour
// budget is measured pre-filter (only the 1080p/fps gate applies server-side),
// while the target is what survives the tab's filters, so a scan stopping
// exactly at the target lands short.
//
// This is now a RUNAWAY GUARD, not the stop. The tab stops a scan the moment
// its FILTERED total reaches the target (see the poll loop), so a scan that is
// finding usable footage ends on its own and never reaches this number --
// which means setting it low buys nothing and costs everything.
//
// Measured: at 1.25 a 1,000h target capped the dig at 1,250h, it stopped there
// with 1,271h raw, and the filters took 316h off -- landing at 955h, 45h short
// with the corpus still holding more. Both earlier values failed the same way,
// from opposite directions: 1.5 wasted minutes when filters were light, 1.25
// truncated when they were heavy. The multiplier was always guessing at a
// number the tab can now measure.
//
// 2x, so the guard only fires on a scan that is genuinely not converging.
const SCAN_HEADROOM = 2;

/** YYYYMMDD-HHMM in the user's local clock — the tail of every bucket name.
 *  Minute precision is deliberate: enough to make several buckets a day, short
 *  enough to read. Same-minute collisions get a numeric suffix server-side. */
const stamp = (d: Date) =>
  `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate(),
  ).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;

export default function DatasetPage() {
  const [data, setData] = useState<VideosResponse | null>(null);
  const [envs, setEnvs] = useState<{ [fn: string]: EnvLabel }>({});
  const [coverage, setCoverage] = useState<EnvCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [excludedCats, setExcludedCats] = useState<string[]>(
    savedFilters?.cats ?? DEFAULT_OFF_CATS,
  );
  const [excludedInds, setExcludedInds] = useState<string[]>(
    savedFilters?.inds ?? DEFAULT_OFF_INDS,
  );
  const [excludedOrgs, setExcludedOrgs] = useState<string[]>(savedFilters?.orgs ?? []);
  // Buckets whose contents are filtered out of everything: browse, stats,
  // search, and the deep scan. Deliberately NOT persisted to localStorage —
  // buckets are shared platform state and a stale selection would quietly
  // shrink someone's results days later with no visible cause.
  const [excludeBuckets, setExcludeBuckets] = useState<string[]>([]);
  // The filenames those buckets hold. Fetched once per selection and reused:
  // the browse list is filtered here in the tab (it already holds every video),
  // while searches are filtered server-side where dropping early is what saves
  // the work.
  const [deliveredFns, setDeliveredFns] = useState<Set<string>>(new Set());
  const [deliveredLoading, setDeliveredLoading] = useState(false);
  const [excludedEnvs, setExcludedEnvs] = useState<string[]>(savedFilters?.envs ?? []);
  const [hideNight, setHideNight] = useState<boolean>(savedFilters?.night ?? false);
  // Off by default: 25,399 of 40,650 videos are 720p and are not
  // delivery-grade, but they are reachable when reach matters more.
  const [allRes, setAllRes] = useState<boolean>(savedFilters?.allRes ?? false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [openSection, setOpenSection] = useState<
    'task' | 'industry' | 'org' | 'env' | 'bucket' | null
  >(null);

  // Visual first and by default: it searches what the footage looks like, which
  // is what most queries here mean. Descriptions only cover hand activity, so a
  // scene query like "garage" finds nothing there (measured: 0 hits).
  const [mode, setMode] = useState<SearchMode>('visual');
  const [queryText, setQueryText] = useState('');
  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [mixBusy, setMixBusy] = useState(false);
  const [mixNote, setMixNote] = useState<string | null>(null);
  const [matched, setMatched] = useState<Set<string> | null>(null);
  const [searchNote, setSearchNote] = useState<string | null>(null);
  // Where in each video the search actually matched. Kept because the filename
  // set alone cannot answer "show me the bit you found" -- the player opens on
  // these, the way plix-soc's AssetDrawer opens on its markers.
  const [markers, setMarkers] = useState<Map<string, Marker[]>>(new Map());
  // filename → which terms found it and how strongly, from the CURRENT search.
  // Copied onto cart items at add time: the cart outlives the search, and the
  // max-hours balancer needs per-term provenance long after markers are gone.
  const [hitInfo, setHitInfo] = useState<
    Map<string, { terms: string[]; score: number }>
  >(new Map());
  const [player, setPlayer] = useState<{
    video: WHVideo;
    url: string;
    marks: Marker[];
    at: number;
  } | null>(null);

  const [activityHits, setActivityHits] = useState<{ [q: string]: SearchHit[] }>({});

  const [manifest, setManifest] = useState<{
    rows: ManifestRow[];
    totals: Totals;
    json: string;
  } | null>(null);
  const [manifestBusy, setManifestBusy] = useState(false);

  const [deliveries, setDeliveries] = useState<DeliveryJob[]>([]);
  // Who the bucket is FOR. Required at checkout: it keys the internal audit log
  // (who got what, selected how), which is what later answers "what has this
  // customer already received". Internal only — never written into the bucket.
  // A dropdown of known customers plus an explicit "new" path, so repeat
  // deliveries reuse the exact same customer key instead of retyping it.
  const [customerChoice, setCustomerChoice] = useState('');
  const [newCustomer, setNewCustomer] = useState('');
  const [knownCustomers, setKnownCustomers] = useState<string[]>([]);
  const customer = customerChoice === NEW_CUSTOMER ? newCustomer : customerChoice;
  // Optional free-text for the audit record ("second batch after their feedback
  // on lighting", ...). Behind a button because most deliveries need none.
  const [notes, setNotes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Where the delivery lands: '' = a new bucket, else the container of an
  // existing bucket to pour into. Appends dedupe against the bucket and merge
  // its manifest/mix server-side, so the container keeps describing all of its
  // contents.
  const [deliverTo, setDeliverTo] = useState('');
  // Co-collect: split the cart into TWO buckets with one joint packing, so the
  // pair opens as few customers as possible between them. A switch, not the
  // default — most deliveries are one bucket.
  const [coOn, setCoOn] = useState(false);
  const [coTermsA, setCoTermsA] = useState<Set<string>>(new Set());
  const [coTargetAText, setCoTargetAText] = useState('');
  const [coTargetBText, setCoTargetBText] = useState('');
  const [deliverToB, setDeliverToB] = useState('');
  // The Buckets-panel "balance / top up" flow: tick two buckets, set a target
  // TOTAL for each, and it pre-arms everything above — destinations, delta
  // targets, category split, exclusions. The banner keeps the armed state
  // visible while the user fills the cart.
  const [balPick, setBalPick] = useState<Set<string>>(new Set());
  const [balTargets, setBalTargets] = useState<{ [c: string]: string }>({});
  const [balanceSetup, setBalanceSetup] = useState<{
    aName: string;
    bName: string | null;
    needA: number;
    needB: number;
    /** Both containers in the run, kept so every delivery it produces can ask
     *  the server to stay disjoint from them — including the single-sided case,
     *  where only one of the two is receiving. */
    pair: string[];
    /** Delivery ids this run produced. Set at checkout so the banner can keep
     *  showing the last step instead of vanishing mid-copy; cleared, with the
     *  whole setup, once they all finish. */
    jobs?: string[];
    /** Containers, kept so the deficits can be recomputed against fresh bucket
     *  sizes after a migration has changed both. */
    aC: string;
    bC: string;
    /** Target TOTAL hours per container, as typed. */
    targets: { [c: string]: number };
    /** 'migrate' pulls footage the OTHER bucket already holds into the themed
     *  one; 'collect' is the ordinary top-up from never-delivered footage. A
     *  run with a donor does migrate first, then collect — moving footage
     *  between buckets shifts the deficit onto the best-effort side, which is
     *  cheaper than finding new footage for the themed one. */
    phase: 'migrate' | 'collect';
    donor: string | null;
    /** The donor's OWN labels for the footage this run took out of it, biggest
     *  loss first. The backfill searches for these: what left was "Refueling
     *  equipment" and "Putting on gloves", so that is what has to come back,
     *  not a fresh guess at what a diversity bucket should hold. */
    backfill?: { term: string; hours: number }[];
    /** Which search engine built the donor, so the backfill uses the same one
     *  rather than assuming. */
    donorEngine?: string;
    /** How far down the lost-category list the backfill has searched. The top
     *  few rarely hold enough hours on their own, so it walks further down
     *  rather than declaring the target unreachable. */
    backfillAt?: number;
  } | null>(null);
  // Restricts the candidate list to one bucket's contents — the inverse of
  // excludeBuckets, and what lets the themed search see what the other bucket
  // is holding during a migration.
  const [onlyBucket, setOnlyBucket] = useState('');
  /** A query to run as soon as the page has re-rendered with the state that
   *  should shape it — arming a migration sets the mode, the restriction and
   *  the terms, and none of those are readable by doSearch until then. */
  const [autoSearch, setAutoSearch] = useState('');
  /** Same idea for the everything-mix: run it once the arming state is in
   *  place. The mix reads Target hours and the exclusions, neither of which is
   *  set at the moment the run is armed. */
  const [autoMix, setAutoMix] = useState(false);
  const [onlyFns, setOnlyFns] = useState<Set<string>>(new Set());
  /** What each donor video counts as inside the donor. Migration reads it to
   *  avoid gutting the categories the donor exists to hold. */
  const [donorCats, setDonorCats] = useState<{ [fn: string]: string[] }>({});

  // Migration selection, donor side: a per-category quota that keeps the donor
  // from being stripped of the breadth it exists to hold. See donorAllowance.
  const donorPick = useMemo(() => {
    const secs = new Map((data?.videos || []).map((v) => [v.filename, v.duration]));
    return donorAllowance(donorCats, (fn) => secs.get(fn) || 0);
  }, [donorCats, data]);

  /** The packer tiebreak for migrated footage: mostly how well the video
   *  matches the receiving bucket, partly how much of that category the donor
   *  can spare. undefined outside a migration, where plain `score` is right. */
  const rankFor = useCallback(
    (fn: string, score?: number) => {
      const spare = donorPick.safety.get(fn);
      return spare === undefined ? undefined : 0.7 * (score ?? 0) + 0.3 * spare;
    },
    [donorPick],
  );
  const { username } = useAuth();

  const [meta, setMeta] = useState<{ filename: string; json: any } | null>(null);
  const [metaBusy, setMetaBusy] = useState(false);

  // ---------- cart ----------
  // The cart is the ONLY thing that ships. Search and filters shape what you
  // browse; adding is an explicit act. That removes the question a
  // "deliver whatever is in view" model can never answer cleanly — what happens
  // to your selection when you change a filter — because the answer is nothing.
  const [cart, setCart] = useState<Map<string, CartItem>>(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (raw) return new Map<string, CartItem>(JSON.parse(raw));
    } catch {
      /* a corrupt cart is not worth failing the page over */
    }
    return new Map();
  });
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [cartWarn, setCartWarn] = useState<string | null>(null);
  // Delivery ceiling, in hours. Empty = no cap. Kept as the raw box contents so
  // typing "1" on the way to "100" doesn't flash a 1-hour cart.
  // Defaulted rather than blank: an empty target means the scan runs to its row
  // budget, which for a broad multi-term query is a ~19-minute dig nobody asked
  // for. 1000h is the usual delivery size and doubles as a runaway guard.
  const [maxHoursText, setMaxHoursText] = useState('1000');
  // Org mix is collapsed by default: the cart row answers "how much", and most
  // of the time that is the whole question. Expand for "from whom".
  const [orgOpen, setOrgOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [envOpen, setEnvOpen] = useState(false);
  const [envOpenCk, setEnvOpenCk] = useState(false);
  const [catOpenCk, setCatOpenCk] = useState(false);
  // Separate from the cart-row toggle: checkout opens clean every time, so the
  // breakdown is there when asked for and not in the way when it isn't.
  const [orgOpenCk, setOrgOpenCk] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // The exhaustive scan for the current visual search, if one was started.
  // Cleared when the query moves on — the server keeps the job file, so
  // re-running the same query later is cheap for us to re-kick.
  const [deep, setDeep] = useState<DeepScanJob | null>(null);
  const [buckets, setBuckets] = useState<Bucket[] | null>(null);
  // Kept in its own state, never merged into `buckets`. Everything that can
  // mutate a container reads `buckets`, so this separation is what makes these
  // rows undeletable rather than a missing button.
  const [external, setExternal] = useState<ExternalBucket[]>([]);
  // Why the delivery list is empty, when it is empty for a reason. Blank and
  // "storage is unreachable" look identical otherwise, and the first reads as
  // "the footage was deleted".
  const [bucketsError, setBucketsError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        FILTER_KEY,
        JSON.stringify({
          cats: excludedCats,
          inds: excludedInds,
          orgs: excludedOrgs,
          envs: excludedEnvs,
          night: hideNight,
          allRes,
        }),
      );
    } catch {
      /* a full quota must not break filtering */
    }
  }, [excludedCats, excludedInds, excludedOrgs, excludedEnvs, hideNight, allRes]);

  // Which of the two ticked buckets is the themed one. FEWER recorded
  // categories = themed: its terms become the category split, and the broader
  // one is both the "everything else" side and the donor a migration pulls
  // from. A bucket with no breakdown cannot name its terms, so it sorts broad.
  const balPair = useMemo(() => {
    if (!buckets || balPick.size !== 2) return null;
    const pair = Array.from(balPick)
      .map((c) => buckets.find((x) => x.container === c))
      .filter(Boolean) as Bucket[];
    if (pair.length !== 2) return null;
    pair.sort(
      (x, y) =>
        (x.breakdown?.categories.length ?? Infinity) -
        (y.breakdown?.categories.length ?? Infinity),
    );
    return { themed: pair[0], broad: pair[1] };
  }, [buckets, balPick]);
  const [bucketsOpen, setBucketsOpen] = useState(false);
  // Which bucket has its mix expanded. One at a time: the panel is a table and
  // two open breakdowns push the rest of the list off screen.
  const [bucketMix, setBucketMix] = useState<string | null>(null);
  const [bucketManifest, setBucketManifest] = useState<{
    container: string;
    text: string;
  } | null>(null);
  // Which reference bucket has its delivery manifest open. Separate state from
  // bucketManifest: that one is a <pre> of somebody else's JSON, this one is an
  // editable ledger, and collapsing them would mean one pane that is sometimes
  // a form.
  const [refManifest, setRefManifest] = useState<ExternalBucket | null>(null);
  // A finished delivery stays on the page until it is dismissed, so you can copy
  // the link — then the page is clear for the next one. It is never lost: the
  // Buckets panel lists every container that exists.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [showRaw, setShowRaw] = useState(false);

  // Survives a reload: assembling 1,000 hours across a dozen searches is real
  // work and a stray refresh should not cost it.
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(Array.from(cart.entries())));
      setCartWarn(null);
    } catch {
      // Roughly 5 MB of localStorage, ~180 bytes an entry — only reachable by
      // adding tens of thousands. The cart still works, it just stops persisting.
      setCartWarn('cart is too large to save — it will not survive a reload');
    }
  }, [cart]);

  const addToCart = useCallback(
    (videos: WHVideo[], via: string) => {
      setCart((prev) => {
        const next = new Map(prev);
        videos.forEach((v) => {
          // First add wins: a video found by "garages" and later by "spraying"
          // keeps the query that actually surfaced it.
          if (next.has(v.filename)) return;
          const inf = hitInfo.get(v.filename);
          next.set(v.filename, {
            filename: v.filename,
            duration: v.duration,
            industry: v.industry,
            orgId: v.orgId,
            orgName: v.orgName,
            addedVia: via,
            terms: inf?.terms.length ? inf.terms : undefined,
            score: inf?.score,
            // Migration only: fold "how little this hurts the donor" into the
            // tiebreak, so trimming 295h down to 174h takes from the donor's
            // deepest categories rather than its thinnest.
            rank: rankFor(v.filename, inf?.score),
          });
        });
        return next;
      });
      setTicked(new Set());
    },
    [hitInfo, rankFor],
  );

  const removeFromCart = useCallback((filenames: string[]) => {
    setCart((prev) => {
      const next = new Map(prev);
      filenames.forEach((f) => next.delete(f));
      return next;
    });
  }, []);

  // What the current search/filters are called in the cart. A free-text search
  // names its own group; an unsearched view is "filters" because that is what
  // actually selected it.
  // Normalised, not the raw box contents: "cars, garage, manufacturing," is the
  // same search as "cars, garage, manufacturing", and a trailing comma should not
  // follow it into the cart label and the manifest's source_query.
  const currentSource = activeQuery
    ? `${mode === 'visual' ? 'visual' : 'desc'}: ${activeQuery
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .join(', ')}`
    : 'filters';

  const cartTotals = useMemo(() => {
    let seconds = 0;
    cart.forEach((c) => {
      seconds += c.duration || 0;
    });
    return {
      videos: cart.size,
      hours: seconds / 3600,
      seconds,
      bytes: seconds * 262916,
    };
  }, [cart]);

  const capSeconds = useMemo(() => {
    const h = parseFloat(maxHoursText);
    return Number.isFinite(h) && h > 0 ? h * 3600 : null;
  }, [maxHoursText]);

  // What actually ships. With no cap (or a cart under it) this IS the cart; with
  // a cap it is the balanced subset — per-term water-fill, best matches first.
  // Everything downstream (tiles, manifest, delivery) reads this, so the cap can
  // never be shown in one place and ignored in another.
  const balance = useMemo(
    () => balanceSelection(Array.from(cart.values()), capSeconds),
    [cart, capSeconds],
  );

  // Every category in the cart — the vocabulary co-collect splits between the
  // two buckets. Same fallback rule the balancer uses for filter-added items.
  const cartTerms = useMemo(() => {
    const s = new Set<string>();
    cart.forEach((c) =>
      (c.terms?.length ? c.terms : [c.addedVia]).forEach((t) => s.add(t)),
    );
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [cart]);

  // The joint packing for co-collect. Null while the setup is incomplete (no
  // terms picked for A, or a target missing) — the checkout says what is
  // missing instead of packing something meaningless.
  const dual = useMemo(() => {
    if (!coOn) return null;
    const a = parseFloat(coTargetAText);
    const b = parseFloat(coTargetBText);
    if (!Number.isFinite(a) || a <= 0 || !Number.isFinite(b) || b <= 0) return null;
    if (!coTermsA.size || coTermsA.size >= cartTerms.length) return null;
    // Orgs already inside the destination buckets are free — the packer draws
    // from them before admitting anyone new (only matters when appending).
    const seed = new Set<string>();
    [deliverTo, deliverToB].forEach((d) => {
      if (!d) return;
      (buckets || [])
        .find((x) => x.container === d)
        ?.breakdown?.orgs.forEach((o) => seed.add(o.orgId));
    });
    return balanceDualSelection(
      Array.from(cart.values()),
      coTermsA,
      a * 3600,
      b * 3600,
      seed,
    );
  }, [coOn, cart, coTermsA, coTargetAText, coTargetBText, cartTerms,
      deliverTo, deliverToB, buckets]);

  const dualStats = useMemo(() => {
    if (!dual) return null;
    const st = (r: BalanceResult) => ({
      videos: r.selected.length,
      hours: r.totalSeconds / 3600,
      orgs: new Set(r.selected.map((i) => i.orgId)).size,
      cats: r.groups.filter((g) => g.pickedSeconds > 0).length,
    });
    return { a: st(dual.a), b: st(dual.b) };
  }, [dual]);

  // Who the footage actually comes from. Shares are of *shipping* seconds, not
  // of video count: a delivery is measured in hours, and one 40-minute video
  // from a single org would otherwise read as "1 of 30 videos, no big deal".
  // Reading off balance.selected (not the cart) means a cap that trims one org
  // away is reflected here instead of quietly disagreeing with the manifest.
  const orgMix = useMemo(() => {
    const g = new Map<string, { orgName: string; videos: number; seconds: number }>();
    balance.selected.forEach((c) => {
      const key = c.orgId || c.orgName || 'unknown';
      const e = g.get(key) || { orgName: c.orgName || c.orgId || 'unknown', videos: 0, seconds: 0 };
      e.videos += 1;
      e.seconds += c.duration || 0;
      g.set(key, e);
    });
    const total = balance.totalSeconds || 1;
    return Array.from(g.entries())
      .map(([orgId, e]) => ({ orgId, ...e, pct: (e.seconds / total) * 100 }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [balance]);

  // Same view by category/label (the balancer's term groups: mix categories,
  // search terms, or "filters"). Shares are of shipping footage, like the org
  // mix — and a multi-term video counts in each of its groups, so shares can
  // sum past 100%; that is the same convention the per-term tallies use.
  const catMix = useMemo(() => {
    const total = balance.totalSeconds || 1;
    return balance.groups
      .filter((g) => g.pickedSeconds > 0)
      .map((g) => ({
        term: g.term,
        seconds: g.pickedSeconds,
        availSeconds: g.availSeconds,
        pct: (g.pickedSeconds / total) * 100,
      }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [balance]);

  // Same view by environment. A partition like orgs — one setting per video —
  // read from the env cache rather than the cart, because the cart never
  // carried it. Answers "where was this shot" before delivery, which was only
  // answerable afterwards from the bucket's own breakdown.
  const envMix = useMemo(() => {
    const g = new Map<string, { seconds: number; videos: number }>();
    balance.selected.forEach((c) => {
      const key = envs[c.filename]?.environment || 'setting unknown';
      const e = g.get(key) || { seconds: 0, videos: 0 };
      e.seconds += c.duration || 0;
      e.videos += 1;
      g.set(key, e);
    });
    const total = balance.totalSeconds || 1;
    return Array.from(g.entries())
      .map(([environment, e]) => ({ environment, ...e, pct: (e.seconds / total) * 100 }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [balance, envs]);

  // Cart contents grouped by the search that produced them — the view that makes
  // a basket assembled from "cars", "garages" and "spraying" legible.
  const cartGroups = useMemo(() => {
    const g = new Map<string, { n: number; seconds: number; files: string[] }>();
    cart.forEach((c) => {
      const e = g.get(c.addedVia) || { n: 0, seconds: 0, files: [] };
      e.n += 1;
      e.seconds += c.duration || 0;
      e.files.push(c.filename);
      g.set(c.addedVia, e);
    });
    return Array.from(g.entries()).sort((a, b) => b[1].seconds - a[1].seconds);
  }, [cart]);

  // ---------- load ----------
  useEffect(() => {
    let live = true;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/wearer-hands/videos?hours=0&min_fps=28` +
            (allRes ? '&allres=1' : ''),
        );
        if (!r.ok) throw new Error(`videos fetch failed (${r.status})`);
        const d = await r.json();
        if (live) setData(d);
      } catch (e: any) {
        if (live) setError(e.message);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [allRes]);

  const loadEnvs = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/environments`);
      if (!r.ok) return;
      const d = await r.json();
      setEnvs(d.environments || {});
      setCoverage(d.coverage || null);
    } catch {
      /* environments are additive; the tab works without them */
    }
  }, []);

  useEffect(() => {
    loadEnvs();
  }, [loadEnvs]);

  // Classification runs in the background on the server, so poll while it is
  // still working rather than showing a number that silently stops being true.
  useEffect(() => {
    if (!coverage || coverage.pending === 0) return;
    const t = setTimeout(loadEnvs, 8000);
    return () => clearTimeout(t);
  }, [coverage, loadEnvs]);

  // Warm every activity query once; the task filter is only meaningful when they
  // have all answered.
  const fetchActivityHits = useCallback(async (q: string) => {
    try {
      // slim: taskOf reads only filename and similarity from these.
      const hits = await runDescriptionSearch(q, ACTIVITY_HIT_LIMIT, true);
      setActivityHits((p) => (q in p ? p : { ...p, [q]: hits }));
    } catch {
      setActivityHits((p) => (q in p ? p : { ...p, [q]: [] }));
    }
  }, []);

  useEffect(() => {
    let stop = false;
    (async () => {
      const queue = TOP_ACTIVITIES.flatMap((a) => a.queries);
      await Promise.all(
        Array.from({ length: 4 }, async () => {
          while (queue.length && !stop) await fetchActivityHits(queue.shift()!);
        }),
      );
    })();
    return () => {
      stop = true;
    };
  }, [fetchActivityHits]);

  // Deliveries that were already finished when the page loaded start dismissed:
  // a delivery card reports what THIS session did, and a bucket that was topped
  // up yesterday should not greet you with a green banner on every refresh. The
  // record is not lost -- it is the Buckets panel.
  const seededDismiss = useRef(false);

  const loadDeliveries = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/deliveries`);
      if (!r.ok) return;
      const d = await r.json();
      const jobs: DeliveryJob[] = d.deliveries || [];
      if (!seededDismiss.current) {
        seededDismiss.current = true;
        const old = jobs
          .filter((j) => !['queued', 'running', 'cancelling'].includes(j.status))
          .map((j) => j.id);
        if (old.length)
          setDismissed((p) => {
            const n = new Set(p);
            old.forEach((id) => n.add(id));
            return n;
          });
      }
      setDeliveries(jobs);
    } catch {
      /* no deliveries yet */
    }
  }, []);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  // Poll only while something is actually moving.
  const anyRunning = deliveries.some((d) =>
    ['queued', 'running', 'cancelling'].includes(d.status),
  );
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(loadDeliveries, POLL_MS);
    return () => clearInterval(t);
  }, [anyRunning, loadDeliveries]);

  // ---------- filter option lists ----------
  // Alphabetical, not by volume: there are only a handful of industries and you
  // come to this list looking for a specific one, so a stable position beats a
  // ranking that reshuffles as the window changes.
  const industryRows = useMemo(() => {
    const n = new Map<string, number>();
    (data?.videos || []).forEach((v) => n.set(v.industry, (n.get(v.industry) || 0) + 1));
    return Array.from(n.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const orgRows = useMemo(() => {
    const n = new Map<string, { name: string; n: number }>();
    (data?.videos || []).forEach((v) => {
      const e = n.get(v.orgId) || { name: v.orgName, n: 0 };
      e.n += 1;
      n.set(v.orgId, e);
    });
    // By display name, like the industry list above: with ~106 orgs you are
    // looking one up, and a volume ranking moves it every time the window does.
    return Array.from(n.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [data]);

  // industry -> the orgs in it, so excluding an industry can check off its orgs.
  const orgsByIndustry = useMemo(() => {
    const m = new Map<string, string[]>();
    (data?.videos || []).forEach((v) => {
      const l = m.get(v.industry) || [];
      if (!l.includes(v.orgId)) l.push(v.orgId);
      m.set(v.industry, l);
    });
    return m;
  }, [data]);

  /**
   * Excluding an industry excludes its organizations too.
   *
   * Not cosmetic: visual search sends `excludeOrgs` to the backend and knows
   * nothing about industries, so without this an industry you had excluded would
   * quietly come back through a CLIP search. It also makes the org list show what
   * is actually happening rather than leaving it looking untouched.
   *
   * Re-including only releases orgs that no OTHER still-excluded industry claims,
   * so unticking one industry cannot silently re-admit another's orgs.
   */
  const toggleIndustry = useCallback(
    (name: string) => {
      const mine = orgsByIndustry.get(name) || [];
      const removing = !excludedInds.includes(name);
      const nextInds = removing
        ? [...excludedInds, name]
        : excludedInds.filter((x) => x !== name);
      setExcludedInds(nextInds);
      setExcludedOrgs((prev) => {
        if (removing) return Array.from(new Set([...prev, ...mine]));
        const stillClaimed = new Set(
          nextInds.flatMap((i) => orgsByIndustry.get(i) || []),
        );
        return prev.filter((o) => !mine.includes(o) || stillClaimed.has(o));
      });
    },
    [excludedInds, orgsByIndustry],
  );

  // Seed the org exclusions from DEFAULT_OFF_INDS once the first payload names
  // the orgs. Runs once: after that the user owns both lists.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || orgsByIndustry.size === 0) return;
    seeded.current = true;
    if (savedFilters) return; // restored state is the user's, not ours to seed
    const byName = new Set(DEFAULT_OFF_ORG_NAMES.map(normOrg));
    const ids = [
      ...excludedInds.flatMap((i) => orgsByIndustry.get(i) || []),
      ...orgRows.filter(([, o]) => byName.has(normOrg(o.name))).map(([id]) => id),
    ];
    if (ids.length) setExcludedOrgs((prev) => Array.from(new Set([...prev, ...ids])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsByIndustry, orgRows]);

  const envRows = useMemo(() => {
    const n = new Map<string, number>();
    (data?.videos || []).forEach((v) => {
      const l = envs[v.filename]?.environment || 'setting unknown';
      n.set(l, (n.get(l) || 0) + 1);
    });
    return Array.from(n.entries()).sort((a, b) => b[1] - a[1]);
  }, [data, envs]);

  // Filenames removed by the task filter. Task removal keys off description-search
  // hits, exactly as the Clips tab does.
  const excludedByTask = useMemo(() => {
    const s = new Set<string>();
    excludedCats.forEach((label) => {
      const a = TOP_ACTIVITIES.find((x) => x.label === label);
      a?.queries.forEach((q) => (activityHits[q] || []).forEach((h) => s.add(h.filename)));
    });
    return s;
  }, [excludedCats, activityHits]);

  // The task each video is best described by, for the manifest. Majority label,
  // tie-broken by similarity — the same rule the Clips tab's manifest uses.
  const taskOf = useMemo(() => {
    const per = new Map<string, Map<string, { c: number; best: number }>>();
    TOP_ACTIVITIES.forEach((a) =>
      a.queries.forEach((q) =>
        (activityHits[q] || []).forEach((h) => {
          const m = per.get(h.filename) || new Map();
          const e = m.get(a.label) || { c: 0, best: 0 };
          e.c += 1;
          e.best = Math.max(e.best, h.similarity);
          m.set(a.label, e);
          per.set(h.filename, m);
        }),
      ),
    );
    const out = new Map<string, string>();
    per.forEach((m, fn) => {
      const top = Array.from(m.entries()).sort(
        (a, b) => b[1].c - a[1].c || b[1].best - a[1].best,
      )[0];
      if (top) out.set(fn, top[0]);
    });
    return out;
  }, [activityHits]);

  const isNight = useCallback(
    (v: WHVideo) =>
      v.nightMedian !== null &&
      data?.nightThreshold != null &&
      v.nightMedian >= data.nightThreshold,
    [data],
  );

  // Category stats for the Stats panel. Respects the active filters (industry,
  // org, environment, task, night) but NOT the current search — it answers
  // "what does the corpus I'm selling from look like", not "what did this query
  // find". Tagged = has a majority task label (same rule the manifest uses),
  // measured in footage seconds because that is the unit footage is sold in.
  // What each filter dimension is holding back, in hours: footage that is not
  // already in an excluded bucket and clears every OTHER filter, so the number
  // is literally "clear this chip and you gain this much". Several of these are
  // on by default -- "Using a phone" alone hides 200h+ of a 1,165h corpus --
  // which is invisible until something asks why the pool is small.
  const filterHolds = useMemo(() => {
    const indSet = new Set(excludedInds);
    const orgSet = new Set(excludedOrgs);
    const envSet = new Set(excludedEnvs);
    const dims = {
      task: (v: WHVideo) => !excludedByTask.has(v.filename),
      industry: (v: WHVideo) => !indSet.has(v.industry),
      customer: (v: WHVideo) => !orgSet.has(v.orgId),
      environment: (v: WHVideo) =>
        !envSet.has(envs[v.filename]?.environment || 'setting unknown'),
      night: (v: WHVideo) => !(hideNight && isNight(v)),
    };
    const keys = Object.keys(dims) as (keyof typeof dims)[];
    const held = new Map<string, number>(keys.map((k) => [k, 0]));
    (data?.videos || []).forEach((v) => {
      if (deliveredFns.has(v.filename)) return; // the bucket tick is not a "filter"
      const failed = keys.filter((k) => !dims[k](v));
      if (failed.length === 1) {
        held.set(failed[0], (held.get(failed[0]) || 0) + v.duration);
      }
    });
    const names: { [k: string]: string } = {
      task: excludedCats.length ? `“${excludedCats.join('”, “')}”` : 'tasks',
      industry: `${excludedInds.length} industries`,
      customer: `${excludedOrgs.length} customers`,
      environment: `${excludedEnvs.length} environments`,
      night: 'night/IR',
    };
    return keys
      .map((k) => ({ key: k, label: names[k], hours: (held.get(k) || 0) / 3600 }))
      .filter((x) => x.hours > 0.5)
      .sort((a, b) => b.hours - a.hours);
  }, [
    data,
    deliveredFns,
    excludedByTask,
    excludedCats,
    excludedInds,
    excludedOrgs,
    excludedEnvs,
    envs,
    hideNight,
    isNight,
  ]);

  // The activity queries answer in the background after page load; until they
  // all have, every task-based number undercounts and should say so.
  const statsWarming = useMemo(() => {
    const qs = TOP_ACTIVITIES.flatMap((a) => a.queries);
    return qs.filter((q) => !(q in activityHits)).length;
  }, [activityHits]);


  // ---------- the selection ----------
  // Every filter EXCEPT the search itself. Extracted so the running deep scan
  // can score its partial hits against exactly what the view will apply — a
  // second copy of this predicate would drift, and the scan would stop on a
  // number the cart then disagreed with.
  const passesFilters = useCallback(() => {
    const indSet = new Set(excludedInds);
    const orgSet = new Set(excludedOrgs);
    const envSet = new Set(excludedEnvs);
    return (v: WHVideo) =>
      (!onlyFns.size || onlyFns.has(v.filename)) &&
      !donorPick.protectedFns.has(v.filename) &&
      !excludedByTask.has(v.filename) &&
      !deliveredFns.has(v.filename) &&
      !indSet.has(v.industry) &&
      !orgSet.has(v.orgId) &&
      !envSet.has(envs[v.filename]?.environment || 'setting unknown') &&
      !(hideNight && isNight(v));
  }, [
    onlyFns,
    donorPick.protectedFns,
    excludedByTask,
    deliveredFns,
    excludedInds,
    excludedOrgs,
    excludedEnvs,
    envs,
    hideNight,
    isNight,
  ]);

  const view = useMemo(() => {
    const all = data?.videos || [];
    const ok = passesFilters();
    const kept = all.filter((v) => (!matched || matched.has(v.filename)) && ok(v));
    const seconds = kept.reduce((a, v) => a + v.duration, 0);
    // Where the footage went, in the order it is taken away: the loaded window,
    // what the excluded buckets already hold, what the filter chips remove, and
    // what is therefore still collectable. `pool` ignores the current search on
    // purpose -- one query's answer says nothing about what another query, or
    // "a bit of everything", could still reach. Stated as a chain because "55h
    // available" out of a 1,165h window is not believable without it.
    let windowSeconds = 0;
    let bucketSeconds = 0;
    let poolSeconds = 0;
    all.forEach((v) => {
      windowSeconds += v.duration;
      if (deliveredFns.has(v.filename)) bucketSeconds += v.duration;
      else if (ok(v)) poolSeconds += v.duration;
    });
    return {
      videos: kept,
      poolSeconds,
      windowSeconds,
      bucketSeconds,
      filterSeconds: windowSeconds - bucketSeconds - poolSeconds,
      totals: {
        videos: kept.length,
        hours: seconds / 3600,
        seconds,
        // Real bytes come back with the manifest; this is the same 0.94 GB/hour
        // measured on real footage, and is only ever shown with a "~".
        bytes: seconds * 262916,
      },
    };
  }, [data, matched, passesFilters, deliveredFns]);

  // The table is paged for render cost, but selection is NOT bounded by the page:
  // the header checkbox ticks every addable video in the view. There is no reason
  // for how many rows we draw to limit what you can pick.
  const [limit, setLimit] = useState(500);
  const shown = useMemo(() => view.videos.slice(0, limit), [view.videos, limit]);

  // Live state of an armed balance run, for the banner: which step it is on,
  // how much of each bucket's gap the cart currently covers, and whether the
  // gap can still be closed.
  //
  // The numbers come from the packer when it can run and from a plain term
  // split before that -- `dual` needs at least one cart term on each side, so
  // it is null for the first few adds, which is exactly when a static banner
  // looks stuck.
  const balanceProgress = useMemo(() => {
    if (!balanceSetup) return null;
    // A migration is one-sided by construction: everything in the cart is
    // headed for the themed bucket, taken out of the other one.
    const migrating = balanceSetup.phase === 'migrate';
    const twoSided = !migrating && !!balanceSetup.bName;
    let gotA = 0;
    let gotB = 0;
    // "Exhausted" = the packer took everything the cart offered and is still
    // short, so no amount of re-balancing helps. That is the difference between
    // "keep collecting" and "this target is not reachable this way".
    let exhausted = false;
    // An EMPTY cart has no groups, and `every` on nothing is true — which read
    // as "this search is used up" before a single video had been added.
    const spent = (gs: { pickedSeconds: number; availSeconds: number }[]) =>
      gs.length > 0 && gs.every((g) => g.pickedSeconds >= g.availSeconds - 1);
    if (twoSided && dual) {
      gotA = dual.a.totalSeconds / 3600;
      gotB = dual.b.totalSeconds / 3600;
      exhausted = spent([...dual.a.groups, ...dual.b.groups]);
    } else if (twoSided) {
      cart.forEach((it) => {
        const terms = it.terms && it.terms.length ? it.terms : [it.addedVia];
        if (terms.some((t) => coTermsA.has(t))) gotA += it.duration || 0;
        else gotB += it.duration || 0;
      });
      gotA /= 3600;
      gotB /= 3600;
      exhausted = false; // no packing yet -- nothing has been ruled out
    } else {
      gotA = balance.totalSeconds / 3600;
      exhausted = spent(balance.groups);
    }
    const sides = [
      { name: balanceSetup.aName, need: balanceSetup.needA, got: gotA },
      ...(twoSided
        ? [{ name: balanceSetup.bName!, need: balanceSetup.needB, got: gotB }]
        : []),
    ];
    // 0.5% of the target is the same slack the packer stops at, so "ready" here
    // agrees with what checkout will actually be able to ship.
    const short = sides.reduce(
      (s, x) => s + Math.max(0, x.need * 0.995 - x.got),
      0,
    );
    const jobs = balanceSetup.jobs || [];
    const live = jobs.length
      ? deliveries.filter((d) => jobs.includes(d.id))
      : [];
    const delivering = live.some((d) =>
      ['queued', 'running', 'cancelling'].includes(d.status),
    );
    // What is still collectable: everything the filters allow, minus what is
    // already in the cart. Deliberately NOT the current search's result -- a
    // search that returns 54h says nothing about whether another term, or "a
    // bit of everything", can find the remaining 175h. Both destination buckets
    // are excluded from this list, so it is genuinely addable footage.
    let cartSeconds = 0;
    cart.forEach((it) => {
      cartSeconds += it.duration || 0;
    });
    const pool = Math.max(0, (view.poolSeconds - cartSeconds) / 3600);
    const hiddenByFilters = view.filterSeconds / 3600;
    // The whole subtraction, so a small `pool` can be checked rather than
    // believed. Sits on the verdict as a tooltip.
    const chain =
      `${fmtHours(view.windowSeconds / 3600)} loaded · ` +
      `${fmtHours(view.bucketSeconds / 3600)} already in the excluded buckets · ` +
      `${fmtHours(hiddenByFilters)} removed by the filter chips · ` +
      `${fmtHours(view.poolSeconds / 3600)} collectable` +
      (cartSeconds ? ` · ${fmtHours(cartSeconds / 3600)} of it in the cart` : '');
    const copied = live.reduce((n, d) => n + d.progress.copied, 0);
    const toCopy = live.reduce((n, d) => n + d.progress.total, 0);

    // The one line that says whether this is working, and if not, what to do.
    // It waits for the collection to settle: judging a pool while the mix is
    // still sweeping categories reports a shortfall against a number that is
    // still climbing.
    const collecting = mixBusy || searching ||
      !!(deep && ['queued', 'running'].includes(deep.status));
    let tone: 'go' | 'ok' | 'stuck' | 'busy' = 'go';
    let note: string;
    if (jobs.length) {
      tone = 'busy';
      note = delivering ? `copying ${copied}/${toCopy}…` : 'delivered';
    } else if (short <= 0) {
      tone = 'ok';
      note = 'targets covered — Checkout is pre-set';
    } else if (collecting) {
      note = `collecting toward ${fmtHours(
        sides.reduce((a, x) => a + x.need, 0),
      )}…`;
    } else if (pool < short) {
      tone = 'stuck';
      note =
        hiddenByFilters > short && filterHolds.length
          ? // Not a real ceiling: the footage exists, the filter chips (several
            // of them on by default) are holding it. Name them, with hours.
            `short ${fmtHours(short)} — ${fmtHours(pool)} passes the filters. ` +
            'Untick ' +
            filterHolds
              .slice(0, 3)
              .map((f) => `${f.label} (+${fmtHours(f.hours)})`)
              .join(' · ')
          : `short ${fmtHours(short)} — only ${fmtHours(pool)} of uncollected ` +
            'footage is left; turn on all resolutions or lower the target';
    } else if (exhausted) {
      // The current search is used up, but the pool is not: another term or
      // the mix shortcut still reaches the target.
      note =
        `short ${fmtHours(short)} — this search is used up, ` +
        `but ${fmtHours(pool)} is still collectable: run another term or ` +
        '“a bit of everything”';
    } else if (migrating) {
      const held = donorPick.protectedFns.size;
      note =
        `taking ${balanceSetup.aName}'s footage out of ${balanceSetup.bName}: ` +
        `${fmtHours(short)} to go — ${fmtHours(pool)} available` +
        (held
          ? `, ${held.toLocaleString()} videos held back to protect its thin ` +
            'categories'
          : '') +
        (cart.size ? '' : ' — add search results to the cart');
    } else {
      note = `short ${fmtHours(short)} — keep collecting (${fmtHours(pool)} available)`;
    }
    return {
      sides,
      short,
      exhausted,
      tone,
      note,
      chain,
      pool,
      collecting,
      // The single filter worth unticking: the biggest hold, and only when it
      // actually moves the needle.
      filterFix: filterHolds.find((f) => f.hours > Math.min(short, 1)) || null,
      step: jobs.length ? 3 : 2,
      delivering,
      migrating,
      exact: !twoSided || !!dual,
    };
  }, [balanceSetup, dual, cart, coTermsA, balance, deliveries, view, filterHolds,
      donorPick, mixBusy, searching, deep]);

  // The run is over once every delivery it started has stopped — except after a
  // migration, which is the FIRST half: both buckets have changed size, so the
  // deficits are recomputed and the run continues into its collect phase.
  useEffect(() => {
    const jobs = balanceSetup?.jobs;
    if (!balanceSetup || !jobs?.length) return;
    const mine = deliveries.filter((d) => jobs.includes(d.id));
    if (mine.length !== jobs.length) return;
    if (mine.some((d) => ['queued', 'running', 'cancelling'].includes(d.status))) return;
    if (balanceSetup.phase !== 'migrate') {
      setBalanceSetup(null);
      return;
    }
    const A = buckets?.find((b) => b.container === balanceSetup.aC);
    const B = buckets?.find((b) => b.container === balanceSetup.bC);
    if (!A || !B) return; // wait for the refreshed sizes rather than guess
    const needA = Math.max(0, balanceSetup.targets[A.container] - bucketHours(A));
    const needB = Math.max(0, balanceSetup.targets[B.container] - bucketHours(B));
    setOnlyBucket('');
    setExcludeBuckets((p) => Array.from(new Set([...p, A.container, B.container])));
    setMaxHoursText(String(Math.max(1, Math.ceil(needA + needB))));
    // Replace what left, by its own name. The donor lost specific categories;
    // searching for those is how the bucket comes back as itself instead of as
    // whatever the next search happens to find. Its own engine, not a guess.
    const lost = (balanceSetup.backfill || []).slice(0, BACKFILL_TERMS).map((x) => x.term);
    if (lost.length) {
      setMode(balanceSetup.donorEngine === 'visual' ? 'visual' : 'desc');
      setQueryText(lost.join(', '));
      setAutoSearch(lost.join(', '));
    } else {
      // Nothing recorded about what left (a donor with no per-video
      // categories): fall back to refilling the short bucket with its own mix.
      setQueryText('');
      const short = needB > 0 ? B : needA > 0 ? A : null;
      if (short) armBackfill(short);
    }
    if (needA > 0 && needB > 0) {
      setCoOn(true);
      setDeliverTo(A.container);
      setDeliverToB(B.container);
      setCoTargetAText(String(Math.ceil(needA)));
      setCoTargetBText(String(Math.ceil(needB)));
      setCoTermsA(new Set((A.breakdown?.categories || []).map((c) => c.term)));
    } else {
      setCoOn(false);
      setDeliverTo((needA > 0 ? A : B).container);
      setDeliverToB('');
    }
    setBalanceSetup({
      ...balanceSetup,
      jobs: undefined,
      phase: 'collect',
      donor: null,
      aName: needA > 0 ? A.name : B.name,
      bName: needA > 0 && needB > 0 ? B.name : null,
      needA: needA > 0 ? needA : needB,
      needB: needA > 0 ? needB : 0,
      backfillAt: lost.length ? BACKFILL_TERMS : 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries, balanceSetup, buckets]);

  // Walk further down the donor's lost categories when the ones searched so far
  // cannot cover the gap. The top five are the biggest losses, not necessarily
  // the ones with footage left to find -- stopping there would report a target
  // as unreachable while half the list was never tried.
  useEffect(() => {
    const bs = balanceSetup;
    if (!bs || bs.phase !== 'collect' || bs.jobs?.length) return;
    const all = bs.backfill || [];
    const at = bs.backfillAt ?? 0;
    if (!at || at >= all.length) return;
    if (!balanceProgress || balanceProgress.short <= 0) return;
    // Only once the current search has actually settled.
    if (searching || autoSearch || (deep && ['queued', 'running'].includes(deep.status))) return;
    const next = all.slice(0, at + BACKFILL_TERMS).map((x) => x.term);
    setQueryText(next.join(', '));
    setAutoSearch(next.join(', '));
    setBalanceSetup((p) => (p ? { ...p, backfillAt: at + BACKFILL_TERMS } : p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceProgress, searching, autoSearch, deep, balanceSetup]);
  const addableInView = useMemo(
    () => view.videos.filter((v) => !cart.has(v.filename)),
    [view.videos, cart],
  );
  const allSelected =
    addableInView.length > 0 && addableInView.every((v) => ticked.has(v.filename));

  // A new search means a new page of candidates; carrying ticks across would let
  // a click land on a row the user never saw.
  useEffect(() => {
    setTicked(new Set());
    setLimit(500);
  }, [activeQuery, mode]);

  // A stale manifest next to a changed selection is a lie, so drop it whenever
  // the selection moves — and the cap is part of the selection: changing it
  // changes what ships.
  const cartKey =
    balance.selected.length + '|' + balance.selected[0]?.filename + '|' + capSeconds;
  const lastKey = useRef(cartKey);
  useEffect(() => {
    if (lastKey.current !== cartKey) {
      lastKey.current = cartKey;
      setManifest(null);
    }
  }, [cartKey]);

  // ---------- search ----------
  // Any new search (either mode) or a clear invalidates the auto-retry of a
  // previous partial visual search, so a stale retry can never clobber what
  // the user is now looking at.
  const searchSeq = useRef(0);

  const doSearch = async (q: string) => {
    const query = q.trim();
    if (!query) return;
    searchSeq.current += 1;
    setSearching(true);
    setError(null);
    setSearchNote(null);
    try {
      if (mode === 'desc') {
        const res = await runDescriptionUnion(query, SEARCH_HIT_LIMIT);
        setMatched(new Set(res.hits.map((h) => h.filename)));
        const m = new Map<string, Marker[]>();
        const info = new Map<string, { terms: string[]; score: number }>();
        res.hits.forEach((h) => {
          m.set(h.filename, [{ t: h.timestamp, label: h.description || '' }]);
          info.set(h.filename, {
            terms: res.termsByFile[h.filename] || [],
            score: h.similarity,
          });
        });
        setMarkers(m);
        setHitInfo(info);
        const per =
          res.perTerm.length > 1
            ? ` — ${res.perTerm.map((t) => `${t.term} ${t.videos}`).join(' · ')}`
            : '';
        // Descriptions only exist for hand activity, so a scene query lands here
        // with almost nothing and no explanation. Say which tool fits instead.
        const hint =
          res.hits.length < 25
            ? ' · descriptions only cover hand activity — try Visual for scenes'
            : '';
        setSearchNote(`${res.hits.length} description matches${per}${hint}`);
      } else {
        const opts = {
          limit: 5000,
          days: 60,
          excludeOrgs: excludedOrgs,
          excludeBuckets,
          require1080p: !allRes,
        };
        const seq = searchSeq.current;
        const apply = (
          r: { hits: VisualHit[]; elapsedS: number; perTerm: { term: string; videos: number }[] },
          tail: string,
        ) => {
          setMatched(new Set(r.hits.map((h) => h.filename)));
          const m = new Map<string, Marker[]>();
          const info = new Map<string, { terms: string[]; score: number }>();
          r.hits.forEach((h) => {
            m.set(
              h.filename,
              h.timestamps.map((t) => ({ t, label: h.matchedTerms.join(', ') })),
            );
            info.set(h.filename, { terms: h.matchedTerms, score: h.score });
          });
          setMarkers(m);
          setHitInfo(info);
          // With several terms the union is the number that matters, but the
          // per-term counts are what tell you which term did the work.
          const per =
            r.perTerm.length > 1
              ? ` — ${r.perTerm.map((t) => `${t.term} ${t.videos}`).join(' · ')}`
              : '';
          setSearchNote(`${r.hits.length} visual matches in ${r.elapsedS}s${per}${tail}`);
        };
        let res = await runVisualSearch(query, opts);
        apply(
          res,
          res.partial ? ' · database was slow — fetching the rest automatically…' : '',
        );
        // The exhaustive scan starts itself — the strip below the note shows
        // its progress, and the results upgrade in place when it lands.
        kickDeepScan(query);
        // A partial scan means Cosmos ran out of time budget, not that the
        // footage isn't there. Retry FOR the user, in the background: results
        // are on screen already, and each retry only ever grows them. The
        // sequence guard drops a stale retry if a new search starts meanwhile.
        if (res.partial) {
          (async () => {
            for (let i = 0; i < 2 && res.partial; i++) {
              let again: typeof res;
              try {
                again = await runVisualSearch(query, opts);
              } catch {
                break;
              }
              if (searchSeq.current !== seq) return;
              // Union by filename, best score wins, matched terms merge — a
              // retry is usually a superset but is not guaranteed to be.
              const by = new Map(res.hits.map((h) => [h.filename, h]));
              (again.hits as VisualHit[]).forEach((h) => {
                const p = by.get(h.filename);
                if (!p) {
                  by.set(h.filename, h);
                  return;
                }
                p.matchedTerms = Array.from(
                  new Set([...p.matchedTerms, ...h.matchedTerms]),
                );
                if (h.score > p.score) {
                  by.set(h.filename, { ...h, matchedTerms: p.matchedTerms });
                }
              });
              const perTerm = res.perTerm.map((t, j) => ({
                term: t.term,
                videos: Math.max(t.videos, again.perTerm[j]?.videos ?? 0),
              }));
              res = {
                ...again,
                perTerm,
                hits: Array.from(by.values()).sort((a, b) => b.score - a.score),
              };
              apply(
                res,
                res.partial
                  ? i < 1
                    ? ' · still fetching…'
                    : ' · database is having a slow day — some results may still be missing'
                  : ' · complete',
              );
            }
          })();
        }
      }
      setActiveQuery(query);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  // Fires the query armBalance queued, once. Placed after doSearch so it can
  // call it, and gated on the donor's contents having arrived -- searching
  // before the restriction is loaded would list the whole corpus.
  useEffect(() => {
    if (!autoSearch) return;
    if (onlyBucket && !onlyFns.size) return;
    if (excludeBuckets.length && (deliveredLoading || !deliveredFns.size)) return;
    const q = autoSearch;
    setAutoSearch('');
    doSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, onlyBucket, onlyFns, excludeBuckets, deliveredLoading, deliveredFns]);

  const clearSearch = () => {
    searchSeq.current += 1;
    setQueryText('');
    setActiveQuery(null);
    setMatched(null);
    setSearchNote(null);
    setMarkers(new Map());
    setHitInfo(new Map());
  };

  // ---------- deep scan ----------
  // A deep scan belongs to one query; the moment the search moves on, its
  // strip goes with it AND the server-side job gets cancelled — an abandoned
  // scan otherwise keeps burning database throughput against the scan the
  // user actually wants (scans run one at a time server-side).
  useEffect(() => {
    if (deep && (mode !== 'visual' || deep.query !== activeQuery)) {
      if (['queued', 'running'].includes(deep.status)) {
        fetch(`${API_BASE_URL}/api/wearer-hands/videosearch/deep/${deep.id}`, {
          method: 'DELETE',
        }).catch(() => {});
      }
      setDeep(null);
    }
  }, [deep, mode, activeQuery]);

  // Auto-kicked by every visual search — the user should never have to ask
  // for completeness. The server dedupes, so re-searching the same query
  // attaches to the scan already running (or just finished) instead of
  // starting a second one.
  const kickDeepScan = async (q?: string) => {
    const query = q ?? activeQuery;
    if (!query) return;
    try {
      const id = await startDeepScan(query, {
        days: 60,
        require1080p: !allRes,
        // Excluded server-side, before the hour budget counts them: the target
        // then means "hours the customer does not already have", and the dig
        // does not spend its rows re-finding footage that would be discarded.
        excludeBuckets,
        // The scan never received the target before, so it fell back to the
        // 10,000h backend default -- a cap that can never fire against a
        // ~3,000h corpus, leaving the row budget as the only stop. Sending it
        // is what bounds a runaway dig.
        //
        // Scaled up by SCAN_HEADROOM because the scan banks hours BEFORE the
        // tab's filters (task, org, night, category), which remove ~30%:
        // stopping at exactly the target would deliver well under it.
        ...(capSeconds != null && {
          maxHours: (capSeconds / 3600) * SCAN_HEADROOM,
        }),
      });
      setDeep({ id, status: 'queued', query, terms: [] });
    } catch (e: any) {
      setError(e.message);
    }
  };

  // Read by the poll loop below without being in its deps: the filters and the
  // video list change constantly, and putting them there would tear down and
  // rebuild the interval on every keystroke.
  const stopCheck = useRef<{
    videos: WHVideo[];
    ok: (v: WHVideo) => boolean;
    capSeconds: number | null;
  }>({ videos: [], ok: () => true, capSeconds: null });
  stopCheck.current = {
    videos: data?.videos || [],
    ok: passesFilters(),
    capSeconds,
  };

  useEffect(() => {
    if (!deep || !['queued', 'running'].includes(deep.status)) return;
    let ticks = 0;
    const t = setInterval(async () => {
      try {
        // Names are ~1/10th of the hit list, so this can ride the poll loop —
        // but not on every tick. Every 4th (10s) matches the backend's own
        // publish cadence, so a faster check would read the same numbers.
        ticks += 1;
        const { videos, ok, capSeconds: cap } = stopCheck.current;
        const wantNames = cap != null && ticks % 4 === 0;
        const j = await getDeepScan(deep.id, false, wantNames);

        // Stop on what survives YOUR filters, not the server's pre-filter
        // total. The server cannot know the task/org/night/category filters,
        // and measured on a car query they removed 48% — which is how a
        // 1,000h target dug past 1,400h and still landed at 779h.
        if (wantNames && j.hitNames && ['queued', 'running'].includes(j.status)) {
          const found = new Set(j.hitNames);
          let usable = 0;
          for (const v of videos) if (found.has(v.filename) && ok(v)) usable += v.duration;
          if (usable >= cap!) {
            // Cancellation is a request, not an abort: the scan finishes its
            // current slice and files everything it has, so nothing found so
            // far is lost.
            fetch(`${API_BASE_URL}/api/wearer-hands/videosearch/deep/${deep.id}`, {
              method: 'DELETE',
            }).catch(() => {});
            setDeep({ ...j, reachedTarget: true });
            return;
          }
        }
        // A stopped scan ends 'cancelled', not 'done', but it files everything
        // it found — so it needs the hit fetch just as much. Without this the
        // results of a scan we stopped ourselves would never reach the table.
        const finished = ['done', 'cancelled'].includes(j.status);
        setDeep(
          finished
            ? { ...(await getDeepScan(deep.id, true)), reachedTarget: deep.reachedTarget }
            : { ...j, reachedTarget: deep.reachedTarget },
        );
      } catch (e: any) {
        // A vanished job (backend restart wiped it) must not poll forever.
        if (String(e?.message).includes('unknown scan')) {
          setDeep({
            ...deep,
            status: 'error',
            error: 'scan lost (backend restarted?) — search again',
          });
        }
        /* other poll failures are transient — keep polling */
      }
    }, 2500);
    return () => clearInterval(t);
  }, [deep]);

  // ---------- everything mix ----------
  // One click, every (non-excluded) category: descriptions first, one simple
  // visual search as spillover for categories the description index barely
  // covers. This is the "diversity" ask labs keep making — a bit of everything.
  // Items enter the cart with terms = [category], so a Max-hours checkout
  // water-fills across CATEGORIES, and the balancer's org-major ordering
  // bin-packs each pick into as few customers as possible — the same packaging
  // rule as everywhere else. Every active filter applies.
  // Below this much description-found footage, a category is "thin" and earns
  // its visual spillover search.
  const MIX_SPILL_S = 25 * 3600;

  const runEverythingMix = async () => {
    if (!data || mixBusy) return;
    setMixBusy(true);
    setMixNote('mixing — collecting description matches…');
    try {
      // The page's own filter predicate, not a copy of it. The copy that used
      // to live here rebuilt every check EXCEPT the bucket exclusions, so the
      // mix happily collected footage the destination bucket already held: the
      // cart looked full, the server dropped it all at create time, and a 175h
      // delivery landed 7h. One predicate, one answer -- it also brings the
      // migration's "only this bucket" restriction along for free.
      const eligible = passesFilters();
      const byFn = new Map(data.videos.map((v) => [v.filename, v]));
      const cats = TOP_ACTIVITIES.filter((a) => !excludedCats.includes(a.label));

      // Phase 1: descriptions — already warmed on page load, zero requests.
      // A video hit by several categories goes to its best-similarity one.
      const found = new Map<string, { cat: string; score: number }>();
      cats.forEach((a) =>
        a.queries.forEach((q) =>
          (activityHits[q] || []).forEach((h) => {
            const v = byFn.get(h.filename);
            if (!v || !eligible(v)) return;
            const prev = found.get(h.filename);
            if (!prev || h.similarity > prev.score)
              found.set(h.filename, { cat: a.label, score: h.similarity });
          }),
        ),
      );
      const catSeconds = new Map<string, number>();
      found.forEach((f, fn) =>
        catSeconds.set(f.cat, (catSeconds.get(f.cat) || 0) + byFn.get(fn)!.duration),
      );

      // Phase 2: visual spillover. A video descriptions already claimed is
      // never reassigned — descriptions know the hand activity, visual only
      // knows the scene.
      //
      // The Max-hours cap is treated as a TARGET, not just a trim: a first
      // shallow sweep (600 rows/category, batched 8 per request — one 8-term
      // union costs barely more than one term) covers every thin category,
      // and if that banks less than the cap plus balancing headroom, a second
      // pass re-scans the categories with the most footage at full depth
      // until the target is met or the corpus under these filters runs out.
      // Headroom matters: the balancer needs surplus to even out categories
      // and to bin-pack customers instead of taking whatever barely fits.
      let foundS = 0;
      found.forEach((_, fn) => (foundS += byFn.get(fn)!.duration));
      const preGate = new Map<string, number>(); // visual term -> pre-gate videos

      const spill = async (
        batch: { label: string; visual?: string }[],
        rows: number,
      ) => {
        try {
          const r = await runVisualSearch(
            batch.map((a) => a.visual!).join(', '),
            {
              limit: 5000,
              days: 60,
              excludeOrgs: excludedOrgs,
              excludeBuckets,
              require1080p: !allRes,
              rowsPerTerm: rows,
            },
          );
          r.perTerm.forEach((t) => preGate.set(t.term, t.videos));
          // The union reports which terms found each video; the first matched
          // term picks the category.
          const catOf = new Map(batch.map((a) => [a.visual!, a.label]));
          (r.hits as VisualHit[]).forEach((h) => {
            const v = byFn.get(h.filename);
            if (!v || !eligible(v) || found.has(h.filename)) return;
            const cat = catOf.get(h.matchedTerms[0]);
            if (!cat) return;
            found.set(h.filename, { cat, score: h.score });
            foundS += v.duration;
          });
        } catch {
          /* one failed batch must not kill the mix */
        }
      };

      const thin = cats.filter(
        (a) => a.visual && (catSeconds.get(a.label) || 0) < MIX_SPILL_S,
      );
      const batches: (typeof thin)[] = [];
      for (let i = 0; i < thin.length; i += 8) batches.push(thin.slice(i, i + 8));
      let spilled = 0;
      for (let i = 0; i < batches.length; i += 2) {
        const round = batches.slice(i, i + 2);
        spilled += round.reduce((a, b) => a + b.length, 0);
        setMixNote(
          `mixing — visual sweep ${Math.min(spilled, thin.length)}/${thin.length}` +
            ` categories · ${fmtHours(foundS / 3600)} found…`,
        );
        await Promise.all(round.map((b) => spill(b, 600)));
      }

      // Deepening pass: only when a cap is set and the sweep fell short.
      //
      // Bounded by what is actually reachable, not just by the target. A
      // backfill asks for the bucket's deficit while most of the corpus is
      // already inside the buckets being topped up, so `foundS < targetS` can
      // be true forever -- and the loop then ran every category at full depth,
      // eleven sequential deep requests, to find nothing. Collecting from
      // scratch hit its target in the first rounds and exited, which is why the
      // same mix felt instant there and took tens of minutes here.
      let poolS = 0;
      data.videos.forEach((v) => {
        if (eligible(v)) poolS += v.duration;
      });
      const targetS =
        capSeconds != null ? Math.min(capSeconds * 1.25, poolS) : null;
      if (targetS != null && foundS < targetS) {
        // Biggest pre-gate yield first — those categories have the most
        // footage left below the sweep's depth. Four terms per request at
        // full depth, one request at a time: deep queries contend on
        // serverless RU, and starving them buys nothing.
        const deepCats = cats
          .filter((a) => a.visual)
          .sort(
            (x, y) => (preGate.get(y.visual!) || 0) - (preGate.get(x.visual!) || 0),
          );
        // ...and stop on diminishing returns, which is the general guard: two
        // consecutive rounds that add less than 1% of the target mean these
        // categories are swept out, whatever the pool estimate said.
        let dry = 0;
        for (let i = 0; i < deepCats.length && foundS < targetS && dry < 2; i += 4) {
          setMixNote(
            `mixing — digging deeper: ${fmtHours(foundS / 3600)} of ` +
              `${fmtHours(targetS / 3600)} available…`,
          );
          const before = foundS;
          await spill(deepCats.slice(i, i + 4), 6000);
          dry = foundS - before < targetS * 0.01 ? dry + 1 : 0;
        }
      }

      // Phase 3: into the cart. First add wins, as everywhere.
      const additions: CartItem[] = [];
      const catsUsed = new Set<string>();
      let seconds = 0;
      found.forEach((f, fn) => {
        if (cart.has(fn)) return;
        const v = byFn.get(fn)!;
        additions.push({
          filename: fn,
          duration: v.duration,
          industry: v.industry,
          orgId: v.orgId,
          orgName: v.orgName,
          addedVia: 'mix: a bit of everything',
          terms: [f.cat],
          score: f.score,
        });
        catsUsed.add(f.cat);
        seconds += v.duration;
      });
      setCart((prev) => {
        const next = new Map(prev);
        additions.forEach((it) => {
          if (!next.has(it.filename)) next.set(it.filename, it);
        });
        return next;
      });
      // When a target was set and the corpus fell short, say so — an
      // unexplained sub-target cart reads as a bug.
      const short =
        capSeconds != null && foundS < capSeconds
          ? ` — the corpus under these filters holds ${fmtHours(foundS / 3600)}, ` +
            `under your ${fmtHours(capSeconds / 3600)} target`
          : ' — checkout evens out categories and bin-packs customers';
      setMixNote(
        `mix added ${additions.length.toLocaleString()} videos · ` +
          `${fmtHours(seconds / 3600)} across ${catsUsed.size} categories${short}`,
      );
    } catch (e: any) {
      setMixNote(`mix failed: ${e.message}`);
    } finally {
      setMixBusy(false);
    }
  };

  // Fires the everything-mix the backfill asked for, once the exclusions and
  // the target it depends on are in state. Gated on the mix not already
  // running so a re-render cannot start a second one.
  useEffect(() => {
    if (!autoMix || mixBusy || statsWarming > 0) return;
    if (excludeBuckets.length && (deliveredLoading || !deliveredFns.size)) return;
    setAutoMix(false);
    runEverythingMix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMix, mixBusy, statsWarming, excludeBuckets, deliveredLoading, deliveredFns]);


  // A finished scan replaces the interactive results for the same query: it is
  // the same search, just complete. Everything downstream — the table, the org
  // filter, add-to-cart, the balancer — works off `matched`/`hitInfo` exactly
  // as before, so the population flows through the existing pipeline.
  useEffect(() => {
    // 'cancelled' counts: a scan the tab stopped because the target was met
    // files its full hit list, and those results are the whole point.
    if (!deep?.hits || !['done', 'cancelled'].includes(deep.status)) return;
    if (mode !== 'visual' || deep.query !== activeQuery) return;
    setMatched(new Set(deep.hits.map((h) => h.filename)));
    const m = new Map<string, Marker[]>();
    const info = new Map<string, { terms: string[]; score: number }>();
    deep.hits.forEach((h) => {
      m.set(
        h.filename,
        h.timestamps.map((t) => ({ t, label: h.matchedTerms.join(', ') })),
      );
      info.set(h.filename, { terms: h.matchedTerms, score: h.score });
    });
    setMarkers(m);
    setHitInfo(info);
    const exhausted = (deep.perTerm || []).every((p) => p.exhausted);
    setSearchNote(
      `deep scan: ${deep.totals!.videos.toLocaleString()} usable videos · ` +
        `${fmtHours(deep.totals!.hours)} in ${Math.round(deep.elapsedS || 0)}s — ` +
        (deep.reachedTarget
          ? `stopped early: enough survives your filters to fill ${fmtHours(
              (capSeconds || 0) / 3600,
            )}`
          : deep.hoursCapped
            ? `stopped at the ${fmtHours(deep.capHours || 0)} cap, there is more`
            : exhausted
              ? 'everything above the quality floor'
              : 'scan budget hit, there may be more'),
    );
  }, [deep, mode, activeQuery, capSeconds]);

  /** Open a video at the point the search matched.
   *
   *  PRE_START mirrors plix-soc's marker panel: land a few seconds early so the
   *  action is approached rather than jumped into halfway. With no search running
   *  there is nothing to seek to, so it opens at the top. */
  const openPlayer = async (video: WHVideo) => {
    setError(null);
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/wearer-hands/sas?filename=${encodeURIComponent(
          video.filename,
        )}`,
      );
      if (!r.ok) throw new Error(`could not open video (${r.status})`);
      const { url } = await r.json();
      const marks = (markers.get(video.filename) || []).slice().sort((a, b) => a.t - b.t);
      setPlayer({
        video,
        url,
        marks,
        at: marks.length ? Math.max(0, marks[0].t - PRE_START) : 0,
      });
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ---------- manifest + delivery ----------
  /** tasks + provenance for everything that ships — the balanced selection, not
   *  the raw cart, so a max-hours cap holds all the way into the bucket. */
  const payloadFor = useCallback(
    (sel: CartItem[]) => {
      const filenames: string[] = [];
      const tasks: { [fn: string]: string } = {};
      const sources: { [fn: string]: string } = {};
      const categories: { [fn: string]: string[] } = {};
      sel.forEach((c) => {
        filenames.push(c.filename);
        const t = taskOf.get(c.filename);
        if (t) tasks[c.filename] = t;
        sources[c.filename] = c.addedVia;
        // The categories that actually matched this video, which is NOT the same
        // as the search that added it: the everything-mix adds every video under
        // one addedVia ("mix: a bit of everything"), so a bucket grouped by
        // source_query showed 1 category for a 40-category delivery. These are
        // the balancer's groups — the same thing the cart's category panel
        // counts — so the bucket and the cart agree.
        if (c.terms?.length) categories[c.filename] = c.terms;
      });
      return { filenames, tasks, sources, categories };
    },
    [taskOf],
  );

  const cartPayload = useCallback(
    () => payloadFor(balance.selected),
    [payloadFor, balance],
  );

  const buildManifest = async () => {
    if (!balance.selected.length) return;
    setManifestBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/delivery/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartPayload()),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `manifest failed (${r.status})`);
      }
      setManifest(await r.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setManifestBusy(false);
    }
  };

  /** Mirrors wh_delivery._slug so the bucket name shown at checkout is the one
   *  that gets created, not an optimistic guess. */
  const slugPreview = (name: string) =>
    (name || '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-')
      .slice(0, 40);

  // Build the manifest as checkout opens: it is the thing being reviewed, so
  // making it a separate button was asking the user to do the app's job.
  const openCheckout = () => {
    setError(null);
    setCheckoutOpen(true);
    setOrgOpenCk(false);
    setCatOpenCk(false);
    // Checkout opens clean: destination and co-collect are per-delivery
    // decisions, not sticky preferences. Unless a balance setup is armed —
    // that flow exists to walk in here with everything pre-set.
    if (!balanceSetup) {
      setDeliverTo('');
      setDeliverToB('');
      setCoOn(false);
      setCoTermsA(new Set());
      setCoTargetAText(maxHoursText || '1000');
      setCoTargetBText(maxHoursText || '1000');
    }
    if (!manifest) buildManifest();
    // Past customers, for the dropdown. Best-effort — and when there are none
    // yet, skip straight to the "new customer" input rather than offering a
    // one-entry menu.
    fetch(`${API_BASE_URL}/api/wearer-hands/audit/customers`)
      .then((r) => r.json())
      .then((d) => {
        const list: string[] = d.customers || [];
        setKnownCustomers(list);
        if (!list.length) setCustomerChoice((c) => c || NEW_CUSTOMER);
      })
      .catch(() => setCustomerChoice((c) => c || NEW_CUSTOMER));
  };

  const downloadManifest = () => {
    if (!manifest) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob([manifest.json], { type: 'application/json' }),
    );
    a.download = `${slugPreview(customer) || 'dataset'}-manifest.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  };

  /** The recorded customer of an existing bucket, if it has one — an append
   *  reuses it so the audit log keeps one customer per bucket. */
  const destCustomer = useCallback(
    (dest: string) =>
      ((buckets || []).find((b) => b.container === dest)?.customer || '').trim(),
    [buckets],
  );

  const createDelivery = async () => {
    setCreating(true);
    setError(null);
    const balancedByTerm = (groups: BalanceGroup[]) =>
      groups.map((g) => ({
        term: g.term,
        videos: g.pickedVideos,
        hours: Math.round((g.pickedSeconds / 3600) * 100) / 100,
        ofVideos: g.availVideos,
        ofHours: Math.round((g.availSeconds / 3600) * 100) / 100,
      }));
    // Recorded in dataset.json so a delivery can be explained later. The
    // balanced selection is what shipped, so its per-term breakdown is the
    // honest record — the live filters are only context.
    const baseFilters = {
      assembledFrom: cartGroups.map(([q, g]) => ({
        query: q,
        videos: g.n,
        hours: Math.round((g.seconds / 3600) * 100) / 100,
      })),
      lastView: {
        excludedInds,
        excludedOrgs,
        excludedCats,
        excludedEnvs,
        hideNight,
        // Which buckets this delivery was assembled AGAINST. Part of the
        // record for the same reason the search terms are: "why is this
        // footage not in here" is otherwise unanswerable later.
        excludeBuckets,
      },
    };
    const postOne = async (body: object) => {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/delivery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `delivery failed (${r.status})`);
      }
      return r.json();
    };
    // delivery-<customer>-<date>-<time>, stamped at the moment of the click in
    // the user's local clock. Nobody names deliveries; the customer + when is
    // the name. Appends carry the target container instead of a name.
    const dest = (to: string, cust: string) =>
      to
        ? { appendTo: to }
        : { name: `${slugPreview(cust)}-${stamp(new Date())}` };
    const started: string[] = [];
    // What actually ships, captured before the cart is emptied.
    const shippedNow = coOn
      ? [...(dual?.a.selected || []), ...(dual?.b.selected || [])]
      : balance.selected;
    try {
      if (coOn) {
        if (!dual) return;
        const sidesSpec = [
          {
            side: 'A',
            res: dual.a,
            to: deliverTo,
            targetH: parseFloat(coTargetAText),
            terms: cartTerms.filter((t) => coTermsA.has(t)),
          },
          {
            side: 'B',
            res: dual.b,
            to: deliverToB,
            targetH: parseFloat(coTargetBText),
            terms: cartTerms.filter((t) => !coTermsA.has(t)),
          },
        ];
        // Sequential on purpose: two new buckets minted in the same minute rely
        // on the server's collision suffix, which reads queued jobs — the first
        // POST must be on disk before the second names itself.
        // Both destinations, so the server re-checks disjointness against the
        // real containers instead of trusting the cart's exclusion filter.
        const pair = Array.from(
          new Set([deliverTo, deliverToB, ...(balanceSetup?.pair || [])]),
        ).filter(Boolean);
        for (const s of sidesSpec) {
          const cust = destCustomer(s.to) || customer.trim();
          const j = await postOne({
            ...dest(s.to, cust),
            disjointFrom: pair,
            customer: cust,
            createdBy: username || undefined,
            notes: notes.trim() || undefined,
            ...payloadFor(s.res.selected),
            filters: {
              ...baseFilters,
              maxHours: s.targetH,
              balancedByTerm: balancedByTerm(s.res.groups),
              coCollect: {
                side: s.side,
                terms: s.terms,
                pairedWith:
                  s.side === 'A'
                    ? deliverToB || 'new bucket'
                    : deliverTo || 'new bucket',
              },
            },
          });
          if (j?.id) started.push(j.id);
        }
      } else {
        if (!balance.selected.length) return;
        const cust = destCustomer(deliverTo) || customer.trim();
        const j = await postOne({
          ...dest(deliverTo, cust),
          // Single-sided balance run: the other bucket receives nothing, but
          // its contents are still off limits to this one.
          ...(balanceSetup && { disjointFrom: balanceSetup.pair }),
          // Migration: the server takes these videos back out of the donor once
          // this delivery has verifiably landed.
          ...(balanceSetup?.phase === 'migrate' &&
            balanceSetup.donor && { migrateFrom: balanceSetup.donor }),
          customer: cust,
          createdBy: username || undefined,
          notes: notes.trim() || undefined,
          ...cartPayload(),
          filters: {
            ...baseFilters,
            ...(capSeconds != null && {
              maxHours: capSeconds / 3600,
              balancedByTerm: balancedByTerm(balance.groups),
            }),
          },
        });
        if (j?.id) started.push(j.id);
      }
      // Shipped: close checkout and empty the cart. The job holds the full row
      // list server-side, so nothing is lost -- and leaving a full cart behind
      // makes it genuinely unclear whether the delivery happened.
      setCheckoutOpen(false);
      setCart(new Map());
      setManifest(null);
      setCustomerChoice('');
      setNewCustomer('');
      setNotes('');
      setNotesOpen(false);
      setDeliverTo('');
      setDeliverToB('');
      setCoOn(false);
      setCoTermsA(new Set());
      // The banner does NOT vanish at checkout: it hands over to its last step
      // and follows the jobs it started, which is the part of the run that
      // actually takes minutes. An effect clears it when they finish.
      //
      // A migration also records what it cost the donor, in the donor's own
      // vocabulary: the categories of the videos that just left, biggest loss
      // first. That list becomes the backfill search.
      const lost = new Map<string, number>();
      if (balanceSetup?.phase === 'migrate') {
        shippedNow.forEach((it) => {
          (donorCats[it.filename] || []).forEach((c) =>
            lost.set(c, (lost.get(c) || 0) + (it.duration || 0) / 3600),
          );
        });
      }
      const backfill = Array.from(lost, ([term, hours]) => ({ term, hours }))
        .sort((a, b) => b.hours - a.hours);
      setBalanceSetup((p) =>
        p ? { ...p, jobs: started, ...(backfill.length && { backfill }) } : null,
      );
      await loadDeliveries();
      loadBuckets(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const loadBuckets = useCallback(async (refresh = false) => {
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/wearer-hands/buckets${refresh ? '?refresh=1' : ''}`,
      );
      if (!r.ok) return;
      const body = await r.json();
      setBuckets(body.buckets || []);
      setExternal(body.external || []);
      setBucketsError(body.bucketsError || null);
    } catch {
      /* the panel shows nothing rather than breaking the page */
    }
  }, []);

  useEffect(() => {
    loadBuckets();
  }, [loadBuckets]);

  // A bucket someone else deleted must not keep filtering: drop selections that
  // are no longer in the list. Buckets are shared, so this is a real case, not
  // just a guard against our own delete button.
  useEffect(() => {
    if (!buckets) return;
    const live = new Set(buckets.map((b) => b.container));
    setExcludeBuckets((prev) =>
      prev.every((c) => live.has(c)) ? prev : prev.filter((c) => live.has(c)),
    );
    // Same for a checkout destination: appending into a bucket someone just
    // deleted would silently mint nothing.
    setDeliverTo((v) => (v && !live.has(v) ? '' : v));
    setDeliverToB((v) => (v && !live.has(v) ? '' : v));
    setBalPick((p) =>
      Array.from(p).every((c) => live.has(c))
        ? p
        : new Set(Array.from(p).filter((c) => live.has(c))),
    );
  }, [buckets]);

  // A delivery finishing changes what the buckets hold: refresh the panel and
  // re-pull the contents of any excluded bucket, which may have just grown (an
  // append into a bucket that is also being filtered against).
  const prevRunningJobs = useRef<Set<string>>(new Set());
  useEffect(() => {
    const running = new Set(
      deliveries
        .filter((d) => ['queued', 'running', 'cancelling'].includes(d.status))
        .map((d) => d.id),
    );
    const finished = Array.from(prevRunningJobs.current).some(
      (id) => !running.has(id),
    );
    prevRunningJobs.current = running;
    if (finished) {
      loadBuckets(true);
      // New array identity re-triggers the contents fetch above.
      setExcludeBuckets((p) => (p.length ? [...p] : p));
    }
  }, [deliveries, loadBuckets]);

  // The donor's contents, for a migration. Same endpoint as the exclusions,
  // used in the opposite direction.
  useEffect(() => {
    if (!onlyBucket) {
      setOnlyFns(new Set());
      setDonorCats({});
      return;
    }
    let cancelled = false;
    fetch(
      `${API_BASE_URL}/api/wearer-hands/buckets/contents?categories=1&containers=` +
        encodeURIComponent(onlyBucket),
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('contents failed'))))
      .then((d) => {
        if (cancelled) return;
        setOnlyFns(new Set<string>(d.filenames || []));
        setDonorCats(d.categories || {});
        if (d.engine) {
          setBalanceSetup((p) => (p ? { ...p, donorEngine: d.engine } : p));
        }
      })
      .catch(() => {
        if (!cancelled) setError('could not read the bucket to migrate from');
      });
    return () => {
      cancelled = true;
    };
  }, [onlyBucket]);

  useEffect(() => {
    if (!excludeBuckets.length) {
      setDeliveredFns(new Set());
      return;
    }
    let cancelled = false;
    setDeliveredLoading(true);
    fetch(
      `${API_BASE_URL}/api/wearer-hands/buckets/contents?containers=` +
        encodeURIComponent(excludeBuckets.join(',')),
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('contents failed'))))
      .then((d) => {
        if (cancelled) return;
        setDeliveredFns(new Set<string>(d.filenames || []));
        // Under-excluding is the dangerous direction — it ships a customer a
        // video they already have — so an unreadable bucket is said out loud.
        if (d.unavailable?.length) {
          setError(`could not read bucket(s): ${d.unavailable.join(', ')}`);
        }
      })
      .catch(() => {
        if (!cancelled) setError('could not load bucket contents');
      })
      .finally(() => {
        if (!cancelled) setDeliveredLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [excludeBuckets]);

  const openBucketManifest = async (b: Bucket) => {
    if (bucketManifest?.container === b.container) {
      setBucketManifest(null); // second click on an open view closes it
      return;
    }
    setBucketManifest({ container: b.container, text: 'loading…' });
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/wearer-hands/buckets/${b.container}/manifest`,
      );
      const text = await r.text();
      setBucketManifest({
        container: b.container,
        text: r.ok ? JSON.stringify(JSON.parse(text), null, 1) : text,
      });
    } catch (e: any) {
      setBucketManifest({ container: b.container, text: String(e) });
    }
  };

  /** The internal audit record: customer, createdBy, search terms, filters and
   *  balance — everything the bucket's own manifest deliberately leaves out. */
  const openBucketAudit = async (b: Bucket) => {
    const title = `${b.container} — audit`;
    if (bucketManifest?.container === title) {
      setBucketManifest(null);
      return;
    }
    setBucketManifest({ container: title, text: 'loading…' });
    try {
      const r = await fetch(`${API_BASE_URL}/api/wearer-hands/audit/${b.jobId}`);
      const j = await r.json();
      setBucketManifest({ container: title, text: JSON.stringify(j, null, 1) });
    } catch (e: any) {
      setBucketManifest({ container: title, text: String(e) });
    }
  };

  // The link on a job record expires; the bucket does not. Mint a fresh one.
  const bucketLink = async (b: Bucket): Promise<string> => {
    const r = await fetch(`${API_BASE_URL}/api/wearer-hands/buckets/${b.container}/sas`, {
      method: 'POST',
    });
    const j = await r.json();
    if (!j.sasUrl) throw new Error(j.error || 'could not mint a link');
    return j.sasUrl;
  };

  /** The same, for the face-blurred copy. Addressed by the DELIVERY container —
   *  the backend derives the partner and re-checks it exists, so a bucket whose
   *  redacted copy vanished between the panel loading and the click fails loudly
   *  instead of copying a link to nothing. */
  const redactedLink = async (b: Bucket): Promise<string> => {
    const r = await fetch(
      `${API_BASE_URL}/api/wearer-hands/buckets/${b.container}/redacted/sas`,
      { method: 'POST' },
    );
    const j = await r.json();
    if (!j.sasUrl) throw new Error(j.error || 'could not mint a redacted link');
    return j.sasUrl;
  };

  /** Closing the panel also drops the open manifest/audit pane. The pane is
   *  state, so without this it survives the close and greets the next open —
   *  possibly describing a bucket that no longer exists. */
  const closeBuckets = () => {
    setBucketsOpen(false);
    setBucketManifest(null);
    setRefManifest(null);
  };

  /** Hours a bucket holds, best source first: job/audit totals, then the
   *  breakdown, then the measured bytes-per-hour constant as a last resort. */
  const bucketHours = (b: Bucket) =>
    b.hours ?? (b.breakdown ? b.breakdown.seconds / 3600 : b.bytes / (262916 * 3600));

  /** Arm a top-up from the Buckets panel: compute what each ticked bucket
   *  still needs, exclude both from all collection, point checkout at them,
   *  and pre-split categories from the themed bucket's recorded breakdown.
   *  When only one bucket actually needs footage this degrades to a plain
   *  append setup — co-collect with a zero target would pack nothing sensibly. */
  /** Point the page at refilling one bucket with more of what it already holds:
   *  its own categories, biggest share first, searched with the engine that
   *  built it. Used both when a fresh run finds one bucket short and when a
   *  migration hands over to its backfill phase. */
  const armBackfill = (b: Bucket) => {
    const cats = (b.breakdown?.categories || [])
      .slice()
      .sort((x, y) => y.seconds - x.seconds)
      .map((c) => ({ term: c.term, hours: c.seconds / 3600 }));
    if (!cats.length) return;
    const terms = cats.slice(0, BACKFILL_TERMS).map((c) => c.term).join(', ');
    // Engine first, then the query: firing the search before knowing whether
    // this bucket was built visually or from descriptions runs it on the wrong
    // index and returns almost nothing.
    fetch(
      `${API_BASE_URL}/api/wearer-hands/buckets/contents?engine=1&containers=` +
        encodeURIComponent(b.container),
    )
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((d) => {
        // A bucket built by the everything-mix is refilled BY the mix. Its top
        // categories are a description of what it holds, not a recipe for more
        // of it -- searching five of forty is how a diversity bucket gets
        // topped up with 55h of the same thing.
        if (d?.engine === 'mix') {
          setQueryText('');
          setAutoMix(true);
        } else {
          setMode(d?.engine === 'visual' ? 'visual' : 'desc');
          setQueryText(terms);
          setAutoSearch(terms);
        }
        setBalanceSetup((p) =>
          p
            ? {
                ...p,
                backfill: cats,
                backfillAt: d?.engine === 'mix' ? 0 : BACKFILL_TERMS,
                donorEngine: d?.engine,
              }
            : p,
        );
      });
  };

  const armBalance = () => {
    if (!balPair) return;
    const { themed: A, broad: B } = balPair;
    const target = (b: Bucket) => parseFloat(balTargets[b.container] ?? '1000') || 0;
    const needA = Math.max(0, target(A) - bucketHours(A));
    const needB = Math.max(0, target(B) - bucketHours(B));
    if (needA <= 0 && needB <= 0) {
      setError('both buckets already hold their targets — nothing to collect');
      return;
    }
    // Both buckets leave the candidate pool either way: a top-up must not
    // re-ship what either of them already holds.
    // Migration first, when the themed bucket is short and the other one has a
    // recorded mix to pull from: its footage may already match the theme, and
    // moving it costs nothing to find. The deficit lands on the best-effort
    // side, which is the whole point of pairing a themed bucket with an
    // "a bit of everything" one.
    const migrating = needA > 0 && B.videos > 0;
    if (migrating) {
      // Only the RECEIVER is excluded, and the donor is actively UN-excluded:
      // a cancelled run leaves its exclusions behind, so arming a migration
      // after one would hide the very bucket it is supposed to take from and
      // report an empty pool.
      setExcludeBuckets((p) =>
        Array.from(new Set([...p.filter((c) => c !== B.container), A.container])),
      );
      setOnlyBucket(B.container);
      setCoOn(false);
      setDeliverTo(A.container);
      setDeliverToB('');
      setMaxHoursText(String(Math.max(1, Math.ceil(needA))));
      // The themed bucket's own terms are the migration query.
      const terms = (A.breakdown?.categories || []).map((c) => c.term).join(', ');
      if (terms) {
        // Engine comes from whichever search built the themed bucket (the
        // donor fetch reports it); until that lands, its terms are visual far
        // more often than not, so that is the opening guess, not a rule.
        setMode('visual');
        setQueryText(terms);
        // ...and run it. Landing on an armed-but-empty view made the step look
        // broken; the whole point of the phase is "here is what the other
        // bucket is holding that you want".
        setAutoSearch(terms);
      }
      setBalanceSetup({
        aName: A.name,
        bName: B.name,
        needA,
        needB,
        pair: [A.container, B.container],
        aC: A.container,
        bC: B.container,
        targets: { [A.container]: target(A), [B.container]: target(B) },
        phase: 'migrate',
        donor: B.container,
      });
      setBalPick(new Set());
      closeBuckets();
      return;
    }
    setExcludeBuckets((p) =>
      Array.from(new Set([...p, A.container, B.container])),
    );
    if (needA > 0 && needB > 0) {
      setCoOn(true);
      setDeliverTo(A.container);
      setDeliverToB(B.container);
      setCoTargetAText(String(Math.ceil(needA)));
      setCoTargetBText(String(Math.ceil(needB)));
      setCoTermsA(new Set((A.breakdown?.categories || []).map((c) => c.term)));
    } else {
      // Single-sided: whichever bucket is short gets a plain append -- and the
      // search that fills it is the bucket's OWN recorded categories, biggest
      // first. Topping a bucket up means more of what it already is; arming
      // with an empty search box left the user to guess that.
      const solo = needA > 0 ? A : B;
      setCoOn(false);
      setDeliverTo(solo.container);
      setDeliverToB('');
      armBackfill(solo);
    }
    // The scans' stop target should chase the combined need, not a stale cap.
    setMaxHoursText(String(Math.max(1, Math.ceil(needA + needB))));
    setBalanceSetup({
      aName: needA > 0 ? A.name : B.name,
      bName: needA > 0 && needB > 0 ? B.name : null,
      needA: needA > 0 ? needA : needB,
      needB: needA > 0 ? needB : 0,
      pair: [A.container, B.container],
      aC: A.container,
      bC: B.container,
      targets: { [A.container]: target(A), [B.container]: target(B) },
      phase: 'collect',
      donor: null,
    });
    setBalPick(new Set());
    closeBuckets();
  };

  const cancelBalance = () => {
    setBalanceSetup(null);
    setCoOn(false);
    setCoTermsA(new Set());
    setDeliverTo('');
    setDeliverToB('');
    setOnlyBucket('');
  };

  const deleteBucket = async (b: Bucket) => {
    if (
      !window.confirm(
        `Delete "${b.name}"?\n\nRemoves the bucket ${b.container} and all ` +
          `${b.videos.toLocaleString()} videos in it. Any link you have shared stops ` +
          `working. The source footage is not touched.`,
      )
    )
      return;
    const r = await fetch(`${API_BASE_URL}/api/wearer-hands/buckets/${b.container}`, {
      method: 'DELETE',
    });
    if (!r.ok) setError((await r.json().catch(() => ({}))).error || 'delete failed');
    // The detail pane may be showing this bucket's manifest or audit record —
    // both are gone now, so a pane left open would describe nothing.
    setBucketManifest((p) =>
      p && (p.container === b.container || p.container === `${b.container} — audit`)
        ? null
        : p,
    );
    // Immediately, not on the next bucket-list load: a deleted bucket that kept
    // filtering would hide footage that is once again free to sell, and the
    // cause would be invisible. (The list-driven prune below covers the case
    // where someone else deletes it.)
    setExcludeBuckets((prev) => prev.filter((c) => c !== b.container));
    setBucketMix((v) => (v === b.container ? null : v));
    loadBuckets(true);
    loadDeliveries();
  };

  const cancelDelivery = async (id: string) => {
    await fetch(`${API_BASE_URL}/api/wearer-hands/delivery/${id}/cancel`, {
      method: 'POST',
    });
    loadDeliveries();
  };

  // Deletes the bucket as well as the record, so it is confirmed rather than
  // one click. Nothing else in this tab destroys data.
  const deleteDelivery = async (d: DeliveryJob) => {
    if (
      !window.confirm(
        d.appendTo
          ? `Delete the record "${d.name}"?\n\nThis was an append into ` +
              `${d.container} — only this job's record goes; the bucket and ` +
              `every video in it stay.`
          : `Delete "${d.name}"?\n\nThis removes the bucket ${d.container} and all ` +
              `${d.totals.videos.toLocaleString()} videos in it. Any SAS link you have ` +
              `shared will stop working. The source footage is not touched.`,
      )
    )
      return;
    const r = await fetch(`${API_BASE_URL}/api/wearer-hands/delivery/${d.id}`, {
      method: 'DELETE',
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      setError(err.error || `delete failed (${r.status})`);
    }
    // This path deletes a bucket too, so it owes the same cleanup as
    // deleteBucket — otherwise the filter keeps hiding footage on behalf of a
    // container that no longer exists.
    setExcludeBuckets((prev) => prev.filter((c) => c !== d.container));
    loadBuckets(true);
    loadDeliveries();
  };

  const openMeta = async (filename: string) => {
    setMetaBusy(true);
    setMeta({ filename, json: null });
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/wearer-hands/metadata?filename=${encodeURIComponent(
          filename,
        )}${taskOf.get(filename) ? `&task=${encodeURIComponent(taskOf.get(filename)!)}` : ''}`,
      );
      const j = await r.json();
      setMeta({ filename, json: r.ok ? j : { error: j.error || r.status } });
    } catch (e: any) {
      setMeta({ filename, json: { error: e.message } });
    } finally {
      setMetaBusy(false);
    }
  };

  const toggle = (arr: string[], set: (v: string[]) => void, k: string) =>
    set(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  const nRemoved =
    excludedCats.length +
    excludedInds.filter((n) => industryRows.some(([x]) => x === n)).length +
    excludedOrgs.length +
    excludedEnvs.length +
    excludeBuckets.length;

  return (
    <div className="ds-page">
      {/* ---------- controls ---------- */}
      <div className="ds-controls">
        <form
          className="ds-search"
          onSubmit={(e) => {
            e.preventDefault();
            doSearch(queryText);
          }}
        >
          {/* A migration searches INSIDE one bucket. Without saying so, the
              themed bucket's terms in the box read as the wrong search
              entirely — they are the right terms aimed at the other bucket's
              contents. */}
          {onlyBucket && (
            <span
              className="ds-only"
              title={`Only footage already in ${onlyBucket.replace('delivery-', '')} is searchable right now — this step moves matching footage out of it`}
            >
              in {onlyBucket.replace('delivery-', '')} only ·{' '}
              {onlyFns.size.toLocaleString()}
            </span>
          )}
          <div className="ds-mode">
            <button
              type="button"
              className={mode === 'visual' ? 'on' : ''}
              onClick={() => setMode('visual')}
              title="Search what the footage looks like (CLIP over frame embeddings)"
            >
              Visual
            </button>
            <button
              type="button"
              className={mode === 'desc' ? 'on' : ''}
              onClick={() => setMode('desc')}
              title="Search the written descriptions of hand activity (MiniLM). Only videos that have been described are searchable this way."
            >
              Descriptions
            </button>
          </div>
          <input
            type="text"
            placeholder={
              mode === 'desc'
                ? 'Search hand activity — commas combine: spraying, drilling'
                : 'Search the footage — commas combine: cars, parking, garage'
            }
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
          <button type="submit" className="ds-searchbtn" disabled={searching}>
            {searching ? <span className="ds-spin" aria-label="Searching" /> : 'Search'}
          </button>
          {activeQuery && (
            <button type="button" onClick={clearSearch}>
              Clear
            </button>
          )}
        </form>

        <label
          className="ds-cap"
          title="How many hours the delivery should be. The mix digs until it finds this much; when the cart holds more, checkout trims to it — balanced across categories, bin-packed by customer."
        >
          Target hours
          <input
            type="number"
            min="0"
            step="1"
            placeholder="∞"
            value={maxHoursText}
            onChange={(e) => setMaxHoursText(e.target.value)}
          />
        </label>

        <button
          type="button"
          className="ds-buckets-btn"
          onClick={() => {
            setBucketsOpen(true);
            loadBuckets(true);
          }}
          title="Delivery buckets that exist in storage right now"
        >
          Buckets{buckets?.length ? ` (${buckets.length})` : ''}
        </button>

        <div className="ds-filter">
          <button type="button" onClick={() => setFilterOpen(!filterOpen)}>
            Filter{nRemoved > 0 ? ` (${nRemoved})` : ''} ▾
          </button>
          {filterOpen && (
            <div className="ds-filter-menu">
              <label className="ds-keep">
                <input
                  type="checkbox"
                  checked={hideNight}
                  onChange={(e) => setHideNight(e.target.checked)}
                />
                Hide night / IR 🌙
              </label>
              <label
                className="ds-keep"
                title="The fleet standard is 1080p; 25,399 of 40,650 videos are 720p. Including them widens search reach but they are not delivery-grade."
              >
                <input
                  type="checkbox"
                  checked={allRes}
                  onChange={(e) => setAllRes(e.target.checked)}
                />
                Include non-1080p
              </label>
              <div className="ds-hint">checked items are removed from the selection</div>

              {(
                [
                  ['task', 'Task', TOP_ACTIVITIES.map((a) => [a.label, 0] as [string, number])],
                  ['industry', 'Industry', industryRows],
                  ['env', 'Environment', envRows],
                ] as const
              ).map(([key, title, rows]) => (
                <React.Fragment key={key}>
                  <div className="ds-divider" />
                  <div
                    className="ds-section"
                    onClick={() => setOpenSection(openSection === key ? null : (key as any))}
                  >
                    <span>{openSection === key ? '▾' : '▸'}</span> {title}
                    {(() => {
                      // Count only exclusions that match something currently
                      // loaded, so a default carried over from an emptier window
                      // cannot inflate the badge.
                      const arr =
                        key === 'task'
                          ? excludedCats
                          : key === 'industry'
                            ? excludedInds
                            : excludedEnvs;
                      const n = arr.filter((x) =>
                        (rows as [string, number][]).some(([name]) => name === x),
                      ).length;
                      return n > 0 ? <span className="ds-on">{n}</span> : null;
                    })()}
                  </div>
                  {openSection === key &&
                    rows.map(([name, n]) => {
                      const arr =
                        key === 'task'
                          ? excludedCats
                          : key === 'industry'
                            ? excludedInds
                            : excludedEnvs;
                      const set =
                        key === 'task'
                          ? setExcludedCats
                          : key === 'industry'
                            ? setExcludedInds
                            : setExcludedEnvs;
                      const nOrgs =
                        key === 'industry' ? (orgsByIndustry.get(name) || []).length : 0;
                      return (
                        <label
                          key={name}
                          title={
                            nOrgs
                              ? `also checks off ${nOrgs} organization${nOrgs > 1 ? 's' : ''}`
                              : undefined
                          }
                        >
                          <input
                            type="checkbox"
                            checked={arr.includes(name)}
                            onChange={() =>
                              key === 'industry'
                                ? toggleIndustry(name)
                                : toggle(arr, set, name)
                            }
                          />
                          {name}
                          {n ? <span className="ds-count">{n}</span> : null}
                        </label>
                      );
                    })}
                </React.Fragment>
              ))}

              <div className="ds-divider" />
              <div
                className="ds-section"
                onClick={() => setOpenSection(openSection === 'org' ? null : 'org')}
              >
                <span>{openSection === 'org' ? '▾' : '▸'}</span> Organization
                {excludedOrgs.length > 0 && (
                  <span className="ds-on">{excludedOrgs.length}</span>
                )}
              </div>
              {openSection === 'org' && (
                <>
                  <div className="ds-actions">
                    <button type="button" onClick={() => setExcludedOrgs(orgRows.map(([id]) => id))}>
                      all
                    </button>
                    <button type="button" onClick={() => setExcludedOrgs([])}>
                      none
                    </button>
                  </div>
                  {orgRows.map(([id, o]) => (
                    <label key={id} title={id}>
                      <input
                        type="checkbox"
                        checked={excludedOrgs.includes(id)}
                        onChange={() => toggle(excludedOrgs, setExcludedOrgs, id)}
                      />
                      {o.name}
                      <span className="ds-count">{o.n}</span>
                    </label>
                  ))}
                </>
              )}

              {/* Already delivered. Unlike every other filter here, this one is
                  not about what the footage IS — it is about what has already
                  been sold. Checking a bucket removes its videos from browse,
                  stats, search and the deep scan, which is also the one filter
                  that makes a scan FASTER: excluded videos never enter the
                  hour budget, so the target counts new footage only. */}
              <div className="ds-divider" />
              <div
                className="ds-section"
                onClick={() => setOpenSection(openSection === 'bucket' ? null : 'bucket')}
              >
                <span>{openSection === 'bucket' ? '▾' : '▸'}</span> Already delivered
                {excludeBuckets.length > 0 && (
                  <span className="ds-on">{excludeBuckets.length}</span>
                )}
              </div>
              {openSection === 'bucket' && (
                <>
                  {excludeBuckets.length > 0 && (
                    <div className="ds-actions">
                      <button type="button" onClick={() => setExcludeBuckets([])}>
                        none
                      </button>
                      <span className="ds-filter-note">
                        {deliveredLoading
                          ? 'loading…'
                          : `${deliveredFns.size.toLocaleString()} videos hidden`}
                      </span>
                    </div>
                  )}
                  {!buckets?.length && (
                    <div className="ds-filter-note">
                      No delivery buckets yet — nothing to exclude.
                    </div>
                  )}
                  {(buckets || []).map((b) => (
                    <label key={b.container} title={b.container}>
                      <input
                        type="checkbox"
                        checked={excludeBuckets.includes(b.container)}
                        onChange={() => toggle(excludeBuckets, setExcludeBuckets, b.container)}
                      />
                      {b.name}
                      <span className="ds-count">{b.videos.toLocaleString()}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <div className="ds-error">{error}</div>}
      {searchNote && !balanceSetup && <div className="ds-note">{searchNote}</div>}

      {/* Deep scan: the interactive search shows a page; this fetches the
          population. Nothing is copied anywhere — the cart holds names, and
          the max-hours balancer picks the strongest subset at checkout. */}
      {mode === 'visual' && activeQuery && deep && !balanceSetup && (
        <div className="ds-deep">
          {['queued', 'running'].includes(deep.status) ? (
            <span className="ds-deep-note">
              <span className="ds-spin" aria-label="Scanning" /> finding
              everything in the background — results above are usable now
              {deep.status === 'queued' && !deep.progress
                ? ' · waiting for another scan to finish'
                : deep.progress
                  ? ` · ${deep.progress.term} (${deep.progress.termIndex + 1}/${deep.progress.terms}): ` +
                    `${deep.progress.rows.toLocaleString()} frames read · ` +
                    `${deep.progress.videos.toLocaleString()} videos · ` +
                    `~${fmtHours(deep.progress.usableHours)} usable`
                  : ''}
            </span>
          ) : deep.status === 'error' ? (
            <span className="ds-deep-note ds-deep-err">
              deep scan failed: {deep.error}{' '}
              <button type="button" onClick={() => kickDeepScan()}>
                retry
              </button>
            </span>
          ) : ['done', 'cancelled'].includes(deep.status) && deep.totals ? (
            <>
              <span className="ds-deep-note">
                found <b>{deep.totals.videos.toLocaleString()}</b> usable videos ·{' '}
                <b>{fmtHours(deep.totals.hours)}</b>
                {deep.reachedTarget
                  ? ` — stopped: enough survives your filters to fill ${fmtHours(
                      (capSeconds || 0) / 3600,
                    )}`
                  : deep.hoursCapped
                    ? ` — stopped at the ${fmtHours(deep.capHours || 0)} cap, there is more`
                    : (deep.perTerm || []).every((p) => p.exhausted)
                      ? ' — everything above the quality floor'
                      : ' — scan budget hit, may be more'}
                {/* The found number is pre-filter; saying so here is what
                    stops "found 1,196h but my cart says 816h" reading as a
                    bug. Driving/phone default-offs bite hard on car queries. */}
                {deep.totals.seconds - view.totals.seconds > 3600 && (
                  <>
                    {' '}· your filters remove{' '}
                    {fmtHours((deep.totals.seconds - view.totals.seconds) / 3600)},
                    leaving <b>{fmtHours(view.totals.seconds / 3600)}</b>
                  </>
                )}
              </span>
              {addableInView.length > 0 && (
                <button
                  type="button"
                  className="ds-deep-btn ds-deep-add"
                  onClick={() => addToCart(addableInView, currentSource)}
                  title="Adds names only. Set Target hours to size the delivery — the strongest matches ship, consolidated by customer."
                >
                  Add all {addableInView.length.toLocaleString()} to cart
                </button>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* The "removed by X" tally is gone: what survived is the number that
          matters, and the tiles already show it. Loading still needs saying. */}
      {loading && <div className="ds-dropped">loading…</div>}

      {/* Armed balance/top-up: stays on screen while the cart is being filled,
          because the setup it carries (destinations, deltas, exclusions) is
          otherwise invisible until checkout. */}
      {balanceSetup && balanceProgress && (
        <div className="ds-balance-banner">
          <div className="ds-bal-steps">
            {balanceSetup.donor ? (
              // The donor is always the one left short afterwards — it just
              // gave footage away. The receiver only needs collecting too if
              // the move could not cover its gap, which the next phase works
              // out from the new bucket sizes rather than promising here.
              <>
                <span className={balanceProgress.step === 2 ? 'now' : 'done'}>
                  take from {balanceSetup.bName}
                </span>
                <span className={balanceProgress.step === 3 ? 'now' : ''}>
                  move
                </span>
                <span>then backfill {balanceSetup.bName}</span>
              </>
            ) : (
              <>
                <span className="done">targets set</span>
                <span className={balanceProgress.step === 2 ? 'now' : 'done'}>
                  collect
                </span>
                <span className={balanceProgress.step === 3 ? 'now' : ''}>
                  deliver
                </span>
              </>
            )}
          </div>

          {/* One gauge per bucket: how much of ITS gap the cart covers. This is
              the number the whole flow exists to move, so it is the number on
              screen — and it changes on every add, which is what says the run
              is alive. */}
          <div className="ds-bal-gauges">
            {balanceProgress.sides.map((s) => {
              const pct = Math.min(100, (s.got / Math.max(s.need, 0.01)) * 100);
              return (
                <div className="ds-bal-gauge" key={s.name}>
                  <b title={s.name}>{s.name}</b>
                  <span className="ds-bal-bar">
                    <i
                      className={pct >= 99.5 ? 'full' : undefined}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="ds-bal-num">
                    {fmtHours(s.got)} / {fmtHours(s.need)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Search progress lives here while a run is armed, instead of in the
              two separate lines above the table that said much the same thing
              in different words. */}
          {(mixBusy || (!activeQuery && !!mixNote)) && balanceProgress.step === 2 && (
            <span className="ds-bal-scan" title={mixNote || undefined}>
              {mixBusy && <span className="ds-spin" aria-label="Mixing" />}
              {mixBusy ? 'mixing…' : `mix found ${fmtHours(cartTotals.hours)}`}
            </span>
          )}
          {activeQuery && balanceProgress.step === 2 && (
            <span className="ds-bal-scan" title={searchNote || undefined}>
              {searching
                ? 'searching…'
                : `${view.totals.videos.toLocaleString()} found · ${fmtHours(
                    view.totals.hours,
                  )}`}
              {deep && ['queued', 'running'].includes(deep.status) && (
                <>
                  {' · '}
                  <span className="ds-spin" aria-label="Scanning" />
                  {deep.progress
                    ? ` still scanning (${deep.progress.termIndex + 1}/${deep.progress.terms})`
                    : ' still scanning'}
                </>
              )}
            </span>
          )}

          <div
            className={`ds-bal-verdict ds-bal-${balanceProgress.tone}`}
            title={balanceProgress.chain}
          >
            {balanceProgress.note}
          </div>

          {/* The verdict is advice, never a gate: a short run is still a
              legitimate run — it just doesn't reach the target this pass. */}
          {/* The two things there are to do, in order, in the one place that is
              already telling you what is going on. */}
          {/* A bucket built by the everything-mix is refilled by it, so the mix
              is THE action for that backfill — it was hidden with the add strip
              and the run looked like it had done nothing. */}
          {/* When a filter chip is what is holding the target back, unticking
              it IS the next step -- it was the one thing on screen that was
              not clickable. */}
          {balanceProgress.step === 2 && balanceProgress.short > 0 &&
            !balanceProgress.collecting && !!balanceProgress.filterFix && (
              <button
                type="button"
                className="ds-bal-add"
                title="Clears that filter and collects again"
                onClick={() => {
                  const f = balanceProgress.filterFix!;
                  if (f.key === 'task') setExcludedCats([]);
                  if (f.key === 'industry') setExcludedInds([]);
                  if (f.key === 'customer') setExcludedOrgs([]);
                  if (f.key === 'environment') setExcludedEnvs([]);
                  if (f.key === 'night') setHideNight(false);
                  if (balanceSetup.donorEngine === 'mix') setAutoMix(true);
                  else if (queryText.trim()) setAutoSearch(queryText.trim());
                }}
              >
                untick {balanceProgress.filterFix.label} (+
                {fmtHours(balanceProgress.filterFix.hours)})
              </button>
            )}
          {balanceProgress.step === 2 && balanceProgress.short > 0 && !mixBusy &&
            !balanceProgress.filterFix &&
            balanceSetup.donorEngine === 'mix' && (
            <button
              type="button"
              className="ds-bal-add"
              disabled={mixBusy || statsWarming > 0 || deliveredLoading}
              title="Every task category at once, up to the target"
              onClick={runEverythingMix}
            >
              {mixBusy && <span className="ds-spin" aria-label="Mixing" />}
              {mixBusy ? 'mixing…' : 'add a bit of everything'}
            </button>
          )}
          {/* The mix only collects what matches one of its categories, and its
              sweep has depth limits -- so it can run out while the pool has
              not. For a best-effort bucket that gap is not a reason to stop:
              anything left that passes the filters is valid footage for it. */}
          {balanceProgress.step === 2 && balanceProgress.short > 0 && !mixBusy &&
            !balanceProgress.filterFix && addableInView.length > 0 && (
            <button
              type="button"
              className="ds-bal-add2"
              title={
                activeQuery
                  ? `Add every result to the cart (${fmtHours(view.totals.hours)})`
                  : 'Adds whatever else passes the filters, category or not — ' +
                    'the target still trims it at checkout'
              }
              onClick={() => addToCart(addableInView, currentSource)}
            >
              {activeQuery
                ? `add all ${addableInView.length.toLocaleString()}`
                : `top up with anything (${fmtHours(
                    addableInView.reduce((a, v) => a + v.duration, 0) / 3600,
                  )})`}
            </button>
          )}
          {balanceProgress.step === 2 && !!cart.size && (
            <button
              type="button"
              className="ds-bal-anyway"
              title={
                balanceProgress.short > 0
                  ? 'Deliver what is in the cart now and top up again later'
                  : 'Everything is pre-set — this just confirms it'
              }
              onClick={openCheckout}
            >
              checkout {fmtHours(balance.totalSeconds / 3600)} →
            </button>
          )}
          {/* At step 3 the deliveries are already running and have their own
              Cancel on the job card — this only clears the banner. */}
          <button type="button" onClick={cancelBalance}>
            {balanceProgress.step === 3 ? 'dismiss' : 'cancel'}
          </button>
        </div>
      )}

      {/* ---------- cart ---------- */}
      {/* One row: what is queued, and the two things you can do with it. The
          per-source breakdown lives in the delivery's dataset.json and in each
          manifest row's source_query, so repeating it here was noise. Per-video
          removal moved to a link on the browse row itself, where the video is. */}
      {cartWarn && <div className="ds-error">{cartWarn}</div>}
      {cart.size > 0 && (
        <div className="ds-cart">
          <div className="ds-cart-row">
            <span className="ds-cart-sum">
              <b>{cartTotals.videos.toLocaleString()}</b> videos ·{' '}
              <b>{fmtHours(cartTotals.hours)}</b>
              {balance.trimmed && (
                <span className="ds-capped">
                  → ships {balance.selected.length.toLocaleString()} ·{' '}
                  {fmtHours(balance.totalSeconds / 3600)} (target{' '}
                  {fmtHours(capSeconds! / 3600)})
                </span>
              )}
              <button
                className={`ds-orgtog${orgOpen ? ' ds-orgtog-on' : ''}`}
                onClick={() => setOrgOpen((v) => !v)}
              >
                {orgMix.length} {orgMix.length === 1 ? 'org' : 'orgs'}
                <span className="ds-caret">{orgOpen ? '▴' : '▾'}</span>
              </button>
              <button
                className={`ds-orgtog${catOpen ? ' ds-orgtog-on' : ''}`}
                onClick={() => setCatOpen((v) => !v)}
              >
                {catMix.length} {catMix.length === 1 ? 'category' : 'categories'}
                <span className="ds-caret">{catOpen ? '▴' : '▾'}</span>
              </button>
              <button
                className={`ds-orgtog${envOpen ? ' ds-orgtog-on' : ''}`}
                onClick={() => setEnvOpen((v) => !v)}
              >
                {envMix.length}{' '}
                {envMix.length === 1 ? 'environment' : 'environments'}
                <span className="ds-caret">{envOpen ? '▴' : '▾'}</span>
              </button>
            </span>
            <button className="ds-clear" onClick={() => setCart(new Map())}>
              Clear
            </button>
            <button className="ds-checkout" onClick={openCheckout}>
              Checkout →
            </button>
          </div>

          {/* Org mix. Shares are of footage seconds, and of what ships — under a
              cap that is the balanced subset, not the whole cart. */}
          {orgOpen && (
            <div className="ds-orgmix">
              <div className="ds-orgmix-head">
                share of {balance.trimmed ? 'shipping' : 'cart'} footage ·{' '}
                {fmtHours(balance.totalSeconds / 3600)} across {orgMix.length}{' '}
                {orgMix.length === 1 ? 'organization' : 'organizations'}
                {/* Without this the count looks like the packer gave up. It
                    packed into the fewest customers that fill the target; these
                    few were opened only so a category would not ship zero. */}
                {balance.coverageOrgs.length > 0 && (
                  <span
                    className="ds-orgmix-note"
                    title="Packed into the fewest organizations that fill the target. These were opened only because a category had no footage in any of them."
                  >
                    {' '}
                    · {balance.coverageOrgs.length} opened for category coverage
                  </span>
                )}
              </div>
              <div className="ds-orgmix-list">
                {orgMix.map((o) => (
                  <div className="ds-orgmix-row" key={o.orgId}>
                    <span className="ds-orgmix-name" title={o.orgId}>
                      {o.orgName}
                    </span>
                    <span className="ds-orgmix-bar">
                      <i style={{ width: `${Math.max(o.pct, 0.6)}%` }} />
                    </span>
                    <span className="ds-orgmix-pct">
                      {o.pct >= 1 ? o.pct.toFixed(0) : o.pct.toFixed(1)}%
                    </span>
                    <span className="ds-orgmix-amt">{fmtHours(o.seconds / 3600)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category mix — same panel, grouped by the balancer's terms. A
              multi-term video counts in each group, so shares can sum past
              100% (same convention as the per-term tallies). */}
          {envOpen && (
            <div className="ds-orgmix">
              <div className="ds-orgmix-head">
                share of {balance.trimmed ? 'shipping' : 'cart'} footage ·{' '}
                {fmtHours(balance.totalSeconds / 3600)} across {envMix.length}{' '}
                {envMix.length === 1 ? 'environment' : 'environments'}
              </div>
              <div className="ds-orgmix-list">
                {envMix.map((e) => (
                  <div className="ds-orgmix-row" key={e.environment}>
                    <span className="ds-orgmix-name" title={e.environment}>
                      {e.environment}
                    </span>
                    <span className="ds-orgmix-bar">
                      <i style={{ width: `${Math.min(Math.max(e.pct, 0.6), 100)}%` }} />
                    </span>
                    <span className="ds-orgmix-pct">
                      {e.pct >= 1 ? e.pct.toFixed(0) : e.pct.toFixed(1)}%
                    </span>
                    <span className="ds-orgmix-amt">{fmtHours(e.seconds / 3600)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {catOpen && (
            <div className="ds-orgmix">
              <div className="ds-orgmix-head">
                share of {balance.trimmed ? 'shipping' : 'cart'} footage ·{' '}
                {fmtHours(balance.totalSeconds / 3600)} across {catMix.length}{' '}
                {catMix.length === 1 ? 'category' : 'categories'}
              </div>
              <div className="ds-orgmix-list">
                {catMix.map((c) => (
                  <div className="ds-orgmix-row" key={c.term}>
                    <span className="ds-orgmix-name" title={c.term}>
                      {c.term}
                    </span>
                    <span className="ds-orgmix-bar">
                      <i style={{ width: `${Math.min(Math.max(c.pct, 0.6), 100)}%` }} />
                    </span>
                    <span className="ds-orgmix-pct">
                      {c.pct >= 1 ? c.pct.toFixed(0) : c.pct.toFixed(1)}%
                    </span>
                    <span className="ds-orgmix-amt">{fmtHours(c.seconds / 3600)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------- deliveries ---------- */}
      {deliveries.filter((d) => !dismissed.has(d.id)).length > 0 && (
        <div className="ds-deliveries">
          {deliveries.filter((d) => !dismissed.has(d.id)).map((d) => (
            <div key={d.id} className={`ds-job ds-job-${d.status}`}>
              <div className="ds-job-head">
                <b>{d.name}</b>
                <span className="ds-job-status">{d.status}</span>
                {/* One line, one story: what went in and how big the bucket is
                    now. The cargo used to be stated twice and the detail --
                    previous size, what was skipped and why -- crowded the card
                    for numbers nobody reads twice. They are the tooltip. */}
                {d.status === 'done' && d.bucketAfter ? (
                  <span
                    className="ds-job-moved"
                    title={[
                      `${fmtBytes(d.totals.bytes)} copied`,
                      d.appendTo && d.bucketBefore
                        ? `held ${fmtHours(d.bucketBefore.hours)} before`
                        : '',
                      d.skippedExisting
                        ? `${d.skippedExisting.toLocaleString()} already in this bucket, skipped`
                        : '',
                      d.skippedElsewhere
                        ? `${d.skippedElsewhere.toLocaleString()} already in the paired bucket, skipped`
                        : '',
                      d.migrated?.removed
                        ? `${d.migrated.removed.toLocaleString()} taken out of the donor`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  >
                    {d.appendTo ? '+' : ''}
                    <b>
                      {d.totals.videos.toLocaleString()} videos ·{' '}
                      {fmtHours(d.totals.hours)}
                    </b>
                    {' → now holds '}
                    <b>
                      {d.bucketAfter.videos.toLocaleString()} ·{' '}
                      {fmtHours(d.bucketAfter.hours)}
                    </b>
                    {!!((d.skippedExisting || 0) + (d.skippedElsewhere || 0)) && (
                      <span className="ds-job-skip">
                        {' · '}
                        {(
                          (d.skippedExisting || 0) + (d.skippedElsewhere || 0)
                        ).toLocaleString()}{' '}
                        skipped, already held
                      </span>
                    )}
                  </span>
                ) : (
                  <span>
                    {d.totals.videos.toLocaleString()} videos ·{' '}
                    {fmtHours(d.totals.hours)} · {fmtBytes(d.totals.bytes)}
                  </span>
                )}
                {d.sasUrl && (
                  <CopyButton
                    className="ds-copybtn"
                    label="Copy link"
                    get={() => d.sasUrl!}
                  />
                )}
                {['queued', 'running'].includes(d.status) ? (
                  <button onClick={() => cancelDelivery(d.id)}>Cancel</button>
                ) : (
                  <>
                    <button
                      className="ds-dismiss"
                      title="Clear this from the page — the bucket stays, listed under Buckets"
                      onClick={() => setDismissed((p) => new Set(p).add(d.id))}
                    >
                      Done
                    </button>
                    <button className="ds-danger" onClick={() => deleteDelivery(d)}>
                      Delete
                    </button>
                  </>
                )}
              </div>
              {d.status === 'running' &&
                (() => {
                  // Track whichever counter is actually moving. During the
                  // issuing phase that is `issued`; `copied` only starts once
                  // every copy has been handed to Azure.
                  const verifying = d.phase === 'verifying';
                  const n = verifying ? d.progress.copied : d.progress.issued;
                  return (
                    <div className="ds-progress">
                      <div
                        className="ds-progress-fill"
                        style={{ width: `${(100 * n) / Math.max(1, d.progress.total)}%` }}
                      />
                      <span>
                        {n}/{d.progress.total}{' '}
                        {verifying ? 'landed' : 'sent to Azure'}
                        {' · '}
                        {d.progress.metadata} metadata
                        {d.progress.failed > 0 && ` · ${d.progress.failed} failed`}
                      </span>
                    </div>
                  );
                })()}
              {d.error && <div className="ds-error">{d.error}</div>}
            </div>
          ))}
        </div>
      )}

      {/* ---------- add strip ---------- */}
      {(!balanceSetup || !!ticked.size) && (
      <div className="ds-add">
        <span className="ds-add-src">
          adding as <b>{currentSource}</b>
        </span>
        {/* One way to select, one way to add. The header checkbox used to tick
            only the rendered page while a second button acted on the whole view,
            so "selected (500)" sat next to "in view (8,077)" and neither number
            explained the other. */}
        <button
          className="ds-primary"
          disabled={!ticked.size}
          onClick={() =>
            addToCart(view.videos.filter((v) => ticked.has(v.filename)), currentSource)
          }
        >
          Add {ticked.size.toLocaleString()} to cart
        </button>
        {ticked.size > 0 && (
          <button onClick={() => setTicked(new Set())}>Clear selection</button>
        )}

        <button
          className="ds-mix-btn"
          disabled={mixBusy || statsWarming > 0}
          onClick={runEverythingMix}
          title="Every task category at once: descriptions first, visual search where descriptions are thin, digging deeper until Target hours is met. Checkout balances categories evenly and bin-packs customers."
        >
          {mixBusy && <span className="ds-spin" aria-label="Mixing" />}
          Add a bit of everything
          {statsWarming > 0 ? ` (warming ${statsWarming}…)` : ''}
        </button>
        {mixNote && <span className="ds-mix-note">{mixNote}</span>}
      </div>
      )}

      {/* ---------- browse table ---------- */}
      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th className="ds-tick">
                <input
                  type="checkbox"
                  title={`Select all ${view.totals.videos.toLocaleString()} videos in view (not just the ${shown.length} shown)`}
                  checked={allSelected}
                  ref={(el) => {
                    // Partly selected is its own state: without this, ticking 500
                    // of 8,077 rows leaves the header looking empty.
                    if (el) el.indeterminate = ticked.size > 0 && !allSelected;
                  }}
                  onChange={(e) =>
                    setTicked(
                      e.target.checked
                        ? new Set(addableInView.map((v) => v.filename))
                        : new Set(),
                    )
                  }
                />
              </th>
              <th>Video</th>
              <th>Environment</th>
              <th>Industry</th>
              <th className="num">Length</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((v) => {
              const inCart = cart.has(v.filename);
              const env = envs[v.filename];
              const nMarks = (markers.get(v.filename) || []).length;
              return (
                <tr key={v.filename} className={inCart ? 'ds-row-in' : undefined}>
                  <td className="ds-tick">
                    <input
                      type="checkbox"
                      checked={ticked.has(v.filename)}
                      disabled={inCart}
                      title={inCart ? 'already in the cart' : undefined}
                      onChange={() =>
                        setTicked((p) => {
                          const n = new Set(p);
                          if (n.has(v.filename)) n.delete(v.filename);
                          else n.add(v.filename);
                          return n;
                        })
                      }
                    />
                  </td>
                  <td className="ds-fn" title={v.filename}>
                    <button className="ds-open" onClick={() => openPlayer(v)}>
                      {v.filename}
                    </button>
                    {nMarks > 0 && (
                      <span
                        className="ds-marks"
                        title={`${nMarks} match${nMarks > 1 ? 'es' : ''} — opens at the first`}
                      >
                        {nMarks} ▸
                      </span>
                    )}
                    {inCart && <span className="ds-in">in cart</span>}
                  </td>
                  <td>
                    {env?.environment || '\u2014'}
                    {env?.source === 'industry' && (
                      <span
                        className="ds-src"
                        title="from the customer's industry, not read from this footage"
                      >
                        ind
                      </span>
                    )}
                  </td>
                  <td>{v.industry}</td>
                  <td className="num">{`${Math.round(v.duration / 60)}m`}</td>
                  <td className="ds-rowacts">
                    <button className="ds-link" onClick={() => openMeta(v.filename)}>
                      metadata
                    </button>
                    {inCart && (
                      <button
                        className="ds-link ds-remove"
                        onClick={() => removeFromCart([v.filename])}
                      >
                        remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {view.videos.length > shown.length && (
          <div className="ds-more">
            showing {shown.length.toLocaleString()} of{' '}
            {view.videos.length.toLocaleString()} \u2014 the header checkbox selects all{' '}
            {view.videos.length.toLocaleString()}, not just these
            <button className="ds-link" onClick={() => setLimit((n) => n + 500)}>
              show 500 more
            </button>
          </div>
        )}
      </div>

      {/* ---------- checkout ---------- */}
      {checkoutOpen && (
        <div className="ds-modal" onClick={() => setCheckoutOpen(false)}>
          <div className="ds-checkout-box" onClick={(e) => e.stopPropagation()}>
            <div className="ds-checkout-head">
              <b>Review &amp; deliver</b>
              <button onClick={() => setCheckoutOpen(false)}>close</button>
            </div>

            {/* One number. The size estimate was a constant times the hours, and
                the video count is already on the Create button — hours is the
                unit a delivery is actually negotiated in. */}
            {coOn && dualStats ? (
              <div className="ds-checkout-hero">
                <span className="ds-hero-n">
                  {fmtHours(dualStats.a.hours)} + {fmtHours(dualStats.b.hours)}
                </span>
                <span className="ds-hero-l">
                  bucket A + bucket B · {dual!.orgs.length}{' '}
                  {dual!.orgs.length === 1 ? 'org' : 'orgs'} between them
                </span>
              </div>
            ) : (
              <div className="ds-checkout-hero">
                <span className="ds-hero-n">{fmtHours(balance.totalSeconds / 3600)}</span>
                <span className="ds-hero-l">total footage</span>
                {catMix.length > 0 && (
                  <button
                    className={`ds-orgtog ds-orgtog-lg${catOpenCk ? ' ds-orgtog-on' : ''}`}
                    onClick={() => setCatOpenCk((v) => !v)}
                  >
                    {catMix.length} {catMix.length === 1 ? 'category' : 'categories'}
                    <span className="ds-caret">{catOpenCk ? '▴' : '▾'}</span>
                  </button>
                )}
                {orgMix.length > 0 && (
                  <button
                    className={`ds-orgtog ds-orgtog-lg${orgOpenCk ? ' ds-orgtog-on' : ''}`}
                    onClick={() => setOrgOpenCk((v) => !v)}
                  >
                    {orgMix.length} {orgMix.length === 1 ? 'org' : 'orgs'}
                    <span className="ds-caret">{orgOpenCk ? '▴' : '▾'}</span>
                  </button>
                )}
                {envMix.length > 0 && (
                  <button
                    className={`ds-orgtog ds-orgtog-lg${envOpenCk ? ' ds-orgtog-on' : ''}`}
                    onClick={() => setEnvOpenCk((v) => !v)}
                  >
                    {envMix.length}{' '}
                    {envMix.length === 1 ? 'environment' : 'environments'}
                    <span className="ds-caret">{envOpenCk ? '▴' : '▾'}</span>
                  </button>
                )}
              </div>
            )}
            {!coOn && balance.trimmed && (
              <div className="ds-hint">
                Target {fmtHours(capSeconds! / 3600)}: trimmed from{' '}
                {cartTotals.videos.toLocaleString()} videos ·{' '}
                {fmtHours(cartTotals.hours)} in the cart — balanced across terms,
                best matches kept.
              </div>
            )}

            {/* Co-collect: two buckets, one packing. The occasional case (a
                themed bucket plus an everything bucket drawn from one corpus),
                so it is a switch, and everything below it only appears once
                it is on. */}
            {cartTerms.length >= 2 && (
              <div className="ds-checkout-sec">
                <label className="ds-keep">
                  <input
                    type="checkbox"
                    checked={coOn}
                    onChange={(e) => setCoOn(e.target.checked)}
                  />
                  Co-collect — split this cart into two buckets
                </label>
                {coOn && (
                  <>
                    <div className="ds-hint">
                      Tick the categories for bucket A; everything else goes to
                      bucket B. A video matching both sides ships once, to
                      whichever bucket is further from its target — and both
                      buckets pack into one shared set of customers.
                    </div>
                    <div className="ds-co-targets">
                      <label>
                        A target hours
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={coTargetAText}
                          onChange={(e) => setCoTargetAText(e.target.value)}
                        />
                      </label>
                      <label>
                        B target hours
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={coTargetBText}
                          onChange={(e) => setCoTargetBText(e.target.value)}
                        />
                      </label>
                    </div>
                    <div className="ds-co-terms">
                      {cartTerms.map((t) => (
                        <label
                          key={t}
                          className={coTermsA.has(t) ? 'ds-co-on' : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={coTermsA.has(t)}
                            onChange={() =>
                              setCoTermsA((p) => {
                                const n = new Set(p);
                                if (n.has(t)) n.delete(t);
                                else n.add(t);
                                return n;
                              })
                            }
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                    {!dual ? (
                      <div className="ds-hint">
                        {!coTermsA.size
                          ? 'Pick at least one category for bucket A.'
                          : coTermsA.size >= cartTerms.length
                            ? 'Leave at least one category for bucket B.'
                            : 'Set both targets to see the split.'}
                      </div>
                    ) : (
                      dualStats && (
                        <div className="ds-hint">
                          A ships {fmtHours(dualStats.a.hours)} ·{' '}
                          {dualStats.a.videos.toLocaleString()} videos ·{' '}
                          {dualStats.a.cats}{' '}
                          {dualStats.a.cats === 1 ? 'category' : 'categories'} ·{' '}
                          {dualStats.a.orgs} {dualStats.a.orgs === 1 ? 'org' : 'orgs'}
                          <br />
                          B ships {fmtHours(dualStats.b.hours)} ·{' '}
                          {dualStats.b.videos.toLocaleString()} videos ·{' '}
                          {dualStats.b.cats}{' '}
                          {dualStats.b.cats === 1 ? 'category' : 'categories'} ·{' '}
                          {dualStats.b.orgs} {dualStats.b.orgs === 1 ? 'org' : 'orgs'}
                          {dualStats.a.hours < parseFloat(coTargetAText) * 0.99 && (
                            <>
                              <br />A lands short of its target — the cart holds no
                              more footage for its categories.
                            </>
                          )}
                          {dualStats.b.hours < parseFloat(coTargetBText) * 0.99 && (
                            <>
                              <br />B lands short of its target — best effort, as
                              intended.
                            </>
                          )}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            )}

            {/* Per-category amounts. This is the answer to "did garage actually
                make it in, or is this all parking" — the one question a capped
                multi-term cart raises. A video matched by several categories
                counts in each row, so shares can sum past 100%. */}
            {envOpenCk && envMix.length > 0 && (
              <div className="ds-checkout-sec">
                <div className="ds-checkout-label">
                  By environment
                  <span className="ds-orgmix-note">
                    where the footage was shot · read from the frames, or the
                    customer's typical setting when it could not be
                  </span>
                </div>
                <div className="ds-orgmix-list">
                  {envMix.map((e) => (
                    <div className="ds-orgmix-row" key={e.environment}>
                      <span className="ds-orgmix-name">{e.environment}</span>
                      <span className="ds-orgmix-bar">
                        <i
                          style={{ width: `${Math.min(Math.max(e.pct, 0.6), 100)}%` }}
                        />
                      </span>
                      <span className="ds-orgmix-pct">
                        {e.pct >= 1 ? e.pct.toFixed(0) : e.pct.toFixed(1)}%
                      </span>
                      <span className="ds-orgmix-amt">
                        {fmtHours(e.seconds / 3600)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {catOpenCk && catMix.length > 0 && (
              <div className="ds-checkout-sec">
                <div className="ds-checkout-label">
                  By category
                  <span className="ds-checkout-sub">
                    share of shipping footage
                  </span>
                </div>
                <div className="ds-checkout-table">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="ds-th-share">Share</th>
                        <th className="num">Footage</th>
                        {balance.trimmed && <th className="num">In cart</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {catMix.map((c) => (
                        <tr key={c.term}>
                          <td className="ds-q" title={c.term}>
                            {c.term}
                          </td>
                          <td>
                            <span className="ds-share-cell">
                              <span className="ds-orgmix-bar">
                                <i
                                  style={{
                                    width: `${Math.min(Math.max(c.pct, 0.6), 100)}%`,
                                  }}
                                />
                              </span>
                              <span className="ds-orgmix-pct">
                                {c.pct >= 1 ? c.pct.toFixed(0) : c.pct.toFixed(1)}%
                              </span>
                            </span>
                          </td>
                          <td className="num">{fmtHours(c.seconds / 3600)}</td>
                          {balance.trimmed && (
                            <td className="num">
                              {fmtHours(c.availSeconds / 3600)}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Who the delivery is drawn from. Same numbers as the cart-row
                dropdown, spelled out here because this is the last screen
                before the footage leaves — and because "how much of this is one
                customer's own data" is a question you want answered before the
                bucket exists, not after. */}
            {orgOpenCk && orgMix.length > 0 && (
              <div className="ds-checkout-sec">
                <div className="ds-checkout-label">
                  By organization
                  <span className="ds-checkout-sub">
                    share of shipping footage
                  </span>
                </div>
                <div className="ds-checkout-table">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Organization</th>
                        <th className="ds-th-share">Share</th>
                        <th className="num">Footage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgMix.map((o) => (
                        <tr key={o.orgId}>
                          <td className="ds-q" title={o.orgId}>
                            {o.orgName}
                          </td>
                          <td>
                            <span className="ds-share-cell">
                              <span className="ds-orgmix-bar">
                                <i style={{ width: `${Math.max(o.pct, 0.6)}%` }} />
                              </span>
                              <span className="ds-orgmix-pct">
                                {o.pct >= 1 ? o.pct.toFixed(0) : o.pct.toFixed(1)}%
                              </span>
                            </span>
                          </td>
                          <td className="num">{fmtHours(o.seconds / 3600)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Where each delivery lands. "New bucket" is the normal case; an
                existing bucket turns the delivery into an append — videos it
                already holds are skipped, and its manifest and mix grow to keep
                describing the whole container. */}
            {(() => {
              const destPicker = (val: string, set: (v: string) => void) => (
                <select
                  className="ds-name ds-cust-sel"
                  value={val}
                  onChange={(e) => set(e.target.value)}
                >
                  <option value="">New bucket</option>
                  {(buckets || []).map((b) => (
                    <option key={b.container} value={b.container}>
                      add to {b.name} ({b.videos.toLocaleString()} videos
                      {b.customer ? ` · ${b.customer}` : ''})
                    </option>
                  ))}
                </select>
              );
              // The typed customer is only needed while some destination is a
              // new bucket (or an old one with no recorded customer) — an
              // append inherits the bucket's customer so the audit log keeps
              // one customer per bucket.
              const needsCustomer = coOn
                ? !destCustomer(deliverTo) || !destCustomer(deliverToB)
                : !destCustomer(deliverTo);
              return (
                <>
                  <div className="ds-checkout-sec">
                    <div className="ds-checkout-label">
                      {coOn ? 'Bucket A' : 'Deliver to'}
                    </div>
                    {destPicker(deliverTo, setDeliverTo)}
                    {deliverTo && (
                      <div className="ds-hint">
                        Adds to <code>{deliverTo}</code>
                        {destCustomer(deliverTo)
                          ? ` — for ${destCustomer(deliverTo)}`
                          : ''}
                        ; videos already in it are skipped.
                      </div>
                    )}
                    {coOn && (
                      <>
                        <div className="ds-checkout-label">Bucket B</div>
                        {destPicker(deliverToB, setDeliverToB)}
                        {deliverToB && (
                          <div className="ds-hint">
                            Adds to <code>{deliverToB}</code>
                            {destCustomer(deliverToB)
                              ? ` — for ${destCustomer(deliverToB)}`
                              : ''}
                            ; videos already in it are skipped.
                          </div>
                        )}
                        {deliverTo !== '' && deliverTo === deliverToB && (
                          <div className="ds-error">
                            A and B point at the same bucket — pick two different
                            destinations.
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {needsCustomer && (
                    <div className="ds-checkout-sec">
                      <div className="ds-checkout-label">Customer</div>
                      <div className="ds-cust-row">
                        <select
                          className="ds-name ds-cust-sel"
                          autoFocus
                          value={customerChoice}
                          onChange={(e) => setCustomerChoice(e.target.value)}
                        >
                          <option value="" disabled>
                            Select customer
                          </option>
                          {knownCustomers.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                          <option value={NEW_CUSTOMER}>+ New customer</option>
                        </select>
                        {customerChoice === NEW_CUSTOMER && (
                          <input
                            className="ds-name"
                            autoFocus
                            value={newCustomer}
                            onChange={(e) => setNewCustomer(e.target.value)}
                          />
                        )}
                      </div>
                      {customer.trim() && !deliverTo && (
                        <div className="ds-hint">
                          Becomes the bucket:{' '}
                          <code>
                            delivery-{slugPreview(customer)}-{stamp(new Date())}
                          </code>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            <div className="ds-checkout-sec">
              {!notesOpen ? (
                <button
                  className="ds-link ds-notes-btn"
                  onClick={() => setNotesOpen(true)}
                >
                  + add notes
                </button>
              ) : (
                <>
                  <div className="ds-checkout-label">
                    Notes
                    <button
                      className="ds-link"
                      onClick={() => {
                        setNotesOpen(false);
                        setNotes('');
                      }}
                    >
                      remove
                    </button>
                  </div>
                  <textarea
                    className="ds-notes"
                    autoFocus
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </>
              )}
            </div>

            {coOn && (
              <div className="ds-checkout-sec">
                <div className="ds-hint">
                  Each bucket gets its own manifest and dataset.json, written at
                  delivery — the single-cart preview below is hidden because it
                  would describe neither.
                </div>
              </div>
            )}
            {!coOn && (
            <div className="ds-checkout-sec">
              <div className="ds-checkout-label">
                Manifest
                {manifest && (
                  <>
                    {/* The table below is paged at 200 rows and reformatted;
                        this is the exact file that ships, all rows. */}
                    <button className="ds-link" onClick={() => setShowRaw(!showRaw)}>
                      {showRaw ? 'hide raw' : 'view raw'}
                    </button>
                    <CopyButton get={() => manifest.json} label="copy" done="copied" />
                    <button className="ds-link" onClick={downloadManifest}>
                      download JSON
                    </button>
                  </>
                )}
              </div>
              {manifest && showRaw && (
                <pre className="ds-raw">{manifest.json}</pre>
              )}
              {manifestBusy && <div className="ds-hint">building…</div>}
              {manifest && (
                <div className="ds-checkout-table">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Video</th>
                        <th>Environment</th>
                        <th>Found by</th>
                        <th className="num">Length</th>
                        <th className="num">Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifest.rows.slice(0, 200).map((r) => (
                        <tr key={r.filename}>
                          <td className="ds-fn" title={r.filename}>
                            {r.filename}
                          </td>
                          <td>
                            {r.environment || '—'}
                            {r.environment_source === 'industry' && (
                              <span
                                className="ds-src"
                                title="from the customer's industry, not read from this footage"
                              >
                                ind
                              </span>
                            )}
                          </td>
                          <td className="ds-q">{r.source_query || '—'}</td>
                          <td className="num">
                            {r.duration_s ? `${Math.round(r.duration_s / 60)}m` : '—'}
                          </td>
                          <td className="num">
                            {r.size_bytes ? fmtBytes(r.size_bytes) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {manifest.rows.length > 200 && (
                    <div className="ds-more">
                      showing 200 of {manifest.rows.length.toLocaleString()} — the CSV and
                      the delivery contain all of them
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {error && <div className="ds-error">{error}</div>}

            <div className="ds-checkout-foot">
              {(() => {
                const needsCustomer = coOn
                  ? !destCustomer(deliverTo) || !destCustomer(deliverToB)
                  : !destCustomer(deliverTo);
                const customerMissing = needsCustomer && !customer.trim();
                const dualBad =
                  coOn &&
                  (!dual ||
                    !dual.a.selected.length ||
                    !dual.b.selected.length ||
                    (deliverTo !== '' && deliverTo === deliverToB));
                const disabled =
                  creating ||
                  customerMissing ||
                  (coOn ? dualBad : manifestBusy || !balance.selected.length);
                const label = creating
                  ? 'Starting…'
                  : coOn
                    ? `Create 2 deliveries (${(
                        (dual?.a.selected.length || 0) +
                        (dual?.b.selected.length || 0)
                      ).toLocaleString()}) →`
                    : deliverTo
                      ? `Add to bucket (${balance.selected.length.toLocaleString()}) →`
                      : `Create delivery (${balance.selected.length.toLocaleString()}) →`;
                return (
                  <button
                    className="ds-primary"
                    onClick={createDelivery}
                    disabled={disabled}
                    title={
                      customerMissing
                        ? 'Say who this delivery is for first'
                        : dualBad
                          ? 'Finish the co-collect setup above first'
                          : !coOn && !balance.selected.length
                            ? 'The max-hours cap is smaller than any single video'
                            : undefined
                    }
                  >
                    {label}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ---------- buckets ---------- */}
      {bucketsOpen && (
        <div className="ds-modal" onClick={closeBuckets}>
          <div className="ds-buckets" onClick={(e) => e.stopPropagation()}>
            <div className="ds-checkout-head">
              <b>Delivery buckets</b>
              {/* Just the count. Sizes are per-row in the table below, and the
                  total was not a number anyone acts on. Nothing at all when the
                  list is empty — the empty state already says so. */}
              <span className="ds-player-meta">
                {!buckets
                  ? 'loading…'
                  : buckets.length === 0
                    ? ''
                    : `${buckets.length} bucket${buckets.length === 1 ? '' : 's'}`}
              </span>
              <button onClick={() => loadBuckets(true)}>refresh</button>
              <button onClick={closeBuckets}>close</button>
            </div>

            {bucketsError && (
              <div className="ds-buckets-err">
                <b>Could not reach delivery storage.</b> The list below is
                incomplete — this is not the same as “no buckets”. {bucketsError}
              </div>
            )}

            {buckets && buckets.length === 0 && external.length === 0 && !bucketsError && (
              <div className="ds-cart-empty">
                No delivery buckets yet. Assemble a cart and check out to create one.
              </div>
            )}

            {/* Two ticked buckets arm the balance strip: a target TOTAL each,
                and one button that walks out of here with checkout pre-set to
                collect the difference into both. */}
            {buckets && balPick.size === 2 && (
              <div className="ds-bal-strip">
                <div className="ds-bal-targets">
                  {Array.from(balPick).map((c) => {
                    const b = buckets.find((x) => x.container === c);
                    if (!b) return null;
                    return (
                      <label key={c} title={c}>
                        <b>{b.name}</b>
                        <span className="ds-bal-now">
                          {fmtHours(bucketHours(b))} →
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={balTargets[c] ?? '1000'}
                          onChange={(e) =>
                            setBalTargets((p) => ({ ...p, [c]: e.target.value }))
                          }
                        />
                        h
                      </label>
                    );
                  })}
                </div>
                {/* Migration is not an option: without it this is just "add to
                    an existing bucket", which the checkout already does. */}
                <button
                  type="button"
                  className="ds-primary"
                  onClick={armBalance}
                  title={
                    balPair
                      ? `Moves footage ${balPair.broad.name} already holds that ` +
                        `matches ${balPair.themed.name}'s terms` +
                        (() => {
                          const t = (
                            balPair.themed.breakdown?.categories || []
                          ).map((c) => c.term);
                          return t.length ? ` (${t.join(', ')})` : '';
                        })() +
                        ', then collects new footage for whatever is still ' +
                        'short. Only the gap for each bucket is collected, the ' +
                        'two stay disjoint, and both manifests are rebuilt on ' +
                        'delivery.'
                      : undefined
                  }
                >
                  Set up collection →
                </button>
              </div>
            )}

            {buckets && (buckets.length > 0 || external.length > 0) && (
              <div className="ds-table-wrap">
                <table className="ds-table">
                  <thead>
                    <tr>
                      <th className="ds-tick" title="Tick two buckets to balance / top them up" />
                      <th>Bucket</th>
                      <th>Customer</th>
                      <th>By</th>
                      <th className="num" title="Click a count to copy a read-only link to that container">
                        Videos
                      </th>
                      {/* Next to Videos on purpose: the two counts side by side
                          are what say whether the redaction run finished. */}
                      <th className="num" title="Face-blurred copy. Click to copy a read-only link to it">
                        Redacted
                      </th>
                      <th className="num">Hours</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {buckets.map((b) => (
                      <React.Fragment key={b.container}>
                      <tr>
                        <td className="ds-tick">
                          <input
                            type="checkbox"
                            checked={balPick.has(b.container)}
                            title={
                              !balPick.has(b.container) && balPick.size >= 2
                                ? 'Balance works on exactly two buckets — untick one first'
                                : 'Select for balance / top up'
                            }
                            disabled={
                              !balPick.has(b.container) && balPick.size >= 2
                            }
                            onChange={() =>
                              setBalPick((p) => {
                                const n = new Set(p);
                                if (n.has(b.container)) n.delete(b.container);
                                else n.add(b.container);
                                return n;
                              })
                            }
                          />
                        </td>
                        <td title={b.container}>
                          <b>{b.name}</b>
                          {(() => {
                            // The deliveries poll is fresher than the bucket
                            // list (it ticks every 2s while anything runs), so
                            // it overrides — a bucket list fetched mid-delivery
                            // was flashing "running" after the job had landed.
                            const st =
                              deliveries.find((x) => x.container === b.container)
                                ?.status ?? b.status;
                            return st && st !== 'done' ? (
                              <span className="ds-job-status">{st}</span>
                            ) : null;
                          })()}
                          {/* The cart's two mix panels, as they were at
                              delivery. Only offered when the delivery recorded
                              one — buckets made before this shipped have no
                              rows left to compute it from. */}
                          {b.breakdown && (
                            <button
                              className={`ds-orgtog${
                                bucketMix === b.container ? ' ds-orgtog-on' : ''
                              }`}
                              title="Organization and category mix of what shipped"
                              onClick={() =>
                                setBucketMix((v) => (v === b.container ? null : b.container))
                              }
                            >
                              {b.breakdown.orgs.length}{' '}
                              {b.breakdown.orgs.length === 1 ? 'org' : 'orgs'} ·{' '}
                              {b.breakdown.categories.length}{' '}
                              {b.breakdown.categories.length === 1
                                ? 'category'
                                : 'categories'}
                              {!!b.breakdown.environments?.length &&
                                ` · ${b.breakdown.environments.length} environments`}
                              <span className="ds-caret">
                                {bucketMix === b.container ? '▴' : '▾'}
                              </span>
                            </button>
                          )}
                        </td>
                        <td>{b.customer || '—'}</td>
                        <td>{b.createdBy || '—'}</td>
                        {/* A bucket that IS its own redacted copy has no
                            un-blurred footage to offer, so the Videos cell
                            dashes out and its single link sits under Redacted,
                            where the column header already says what it is.
                            Relabelling the pill in place instead put a purple
                            "redacted" control under Videos beside a dash under
                            Redacted, which read as a bug. */}
                        <td className="num ds-pillcell">
                          {b.contentsRedacted ? (
                            <span
                              className="ds-nored"
                              title="This bucket holds redacted footage only — there is no un-blurred copy"
                            >
                              —
                            </span>
                          ) : (
                            <LinkPill
                              kind="src"
                              count={b.videos}
                              get={() => bucketLink(b)}
                              title={
                                `Copy a read-only link to ${b.container} — ` +
                                `${b.videos.toLocaleString()} ORIGINAL, un-blurred videos`
                              }
                            />
                          )}
                        </td>
                        {/* The face-blurred copy. Nothing at all when there is
                            none -- a disabled control would still read as "this
                            bucket has a redacted version, somehow greyed out",
                            and the sample bucket simply does not have one. */}
                        <td className="num ds-pillcell">
                          {b.contentsRedacted ? (
                            <LinkPill
                              kind="red"
                              count={b.videos}
                              get={() => bucketLink(b)}
                              title={
                                `Copy a read-only link to ${b.container} — ` +
                                `${b.videos.toLocaleString()} redacted videos. ` +
                                'This bucket is itself the redacted copy.'
                              }
                            />
                          ) : b.redacted ? (
                            <>
                              <LinkPill
                                kind="red"
                                count={b.redacted.videos}
                                get={() => redactedLink(b)}
                                title={
                                  `Copy a read-only link to ${b.redacted.container} — ` +
                                  `${b.redacted.videos.toLocaleString()} face-blurred videos` +
                                  (b.redacted.videos === b.videos
                                    ? ''
                                    : `, and this bucket holds ${b.videos.toLocaleString()}: ` +
                                      'the redaction run is incomplete')
                                }
                              />
                              {b.redacted.videos !== b.videos && (
                                <span
                                  className="ds-redpartial"
                                  title="Fewer redacted videos than the bucket holds"
                                >
                                  partial
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="ds-nored" title="No redacted copy of this bucket">
                              —
                            </span>
                          )}
                        </td>
                        {/* The unit targets are set in. It was only readable by
                            opening the bucket's mix panel. */}
                        <td className="num">{fmtHours(bucketHours(b))}</td>
                        <td>
                          {b.lastModified ? b.lastModified.slice(0, 16).replace('T', ' ') : '—'}
                        </td>
                        <td className="ds-rowacts">
                          <button
                            className="ds-link"
                            disabled={!b.hasManifest}
                            onClick={() => openBucketManifest(b)}
                          >
                            manifest
                          </button>
                          <button
                            className="ds-link"
                            disabled={!b.jobId}
                            title={
                              b.jobId
                                ? 'Internal record: customer, search terms, filters'
                                : 'No audit record for this bucket'
                            }
                            onClick={() => openBucketAudit(b)}
                          >
                            audit
                          </button>
                          {/* The source link moved into the Videos column, next
                              to the redacted one: they are the same fact about
                              two containers, and leaving one here while the
                              other sat four cells away was what made the pill
                              look like a badge rather than a control. */}
                          <button
                            className="ds-link ds-remove"
                            onClick={() => deleteBucket(b)}
                          >
                            delete
                          </button>
                        </td>
                      </tr>
                      {bucketMix === b.container && b.breakdown && (
                        <tr className="ds-bucket-mixrow">
                          <td colSpan={9}>
                            <BucketMix breakdown={b.breakdown} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  {/* Reference buckets. A separate <tbody> rather than more rows
                      in the one above, so the divider is structural and nothing
                      that maps over the delivery rows can reach these. There is
                      no delete control here and there is no code path to one:
                      these are GCS, the delete route only speaks Azure, and its
                      guard requires a prefix none of these names carry. */}
                  {external.length > 0 && (
                    <tbody className="ds-extbody">
                      <tr className="ds-extsep">
                        <td colSpan={9}>
                          <span className="ds-extsep-label">Reference buckets</span>
                        </td>
                      </tr>
                      {external.map((x) => (
                        <tr key={x.id} className="ds-extrow">
                          <td className="ds-tick" />
                          <td title={`gs://${x.bucket}/${x.prefix}`}>
                            <b>{x.name}</b>
                          </td>
                          {/* Dashes, not blanks: these buckets have no customer
                              and no creator, and an empty cell reads as "we
                              failed to look it up". */}
                          <td>—</td>
                          <td>—</td>
                          <td className="num ds-pillcell">
                            <span className="ds-pillstack">
                              <span
                                className="ds-pillcount"
                                title={
                                  x.error
                                    ? x.error
                                    : `${(x.objects ?? 0).toLocaleString()} objects · ` +
                                      `${fmtBytes(x.bytes ?? 0)}` +
                                      (x.truncated
                                        ? ' (listing capped — count is a floor)'
                                        : '')
                                }
                              >
                                {x.videos === null
                                  ? '—'
                                  : `${x.truncated ? '≥' : ''}${x.videos.toLocaleString()}`}
                              </span>
                              <a
                                className="ds-pill ds-pill-ext"
                                href={x.url}
                                target="_blank"
                                rel="noreferrer"
                                title={`Open gs://${x.bucket}/${x.prefix} in the Google Cloud console`}
                              >
                                <svg
                                  className="ds-pillicon"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.6"
                                  strokeLinecap="round"
                                  aria-hidden="true"
                                >
                                  <path d="M6.6 9.4a3 3 0 0 0 4.24 0l2.12-2.12a3 3 0 0 0-4.24-4.24l-1 1" />
                                  <path d="M9.4 6.6a3 3 0 0 0-4.24 0L3.04 8.72a3 3 0 0 0 4.24 4.24l1-1" />
                                </svg>
                                open in gcs
                              </a>
                            </span>
                          </td>
                          <td className="num">—</td>
                          {/* Hours deliberately blank for now. */}
                          <td className="num">—</td>
                          <td>—</td>
                          {/* manifest only. There is deliberately no delete
                              here and no code path to one -- see the tbody
                              comment above. */}
                          <td className="ds-rowacts">
                            <button
                              className="ds-link"
                              title="Delivery metadata: what this bucket can prove, and what is still missing"
                              onClick={() =>
                                setRefManifest((v) => (v?.id === x.id ? null : x))
                              }
                            >
                              manifest
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  )}
                </table>
              </div>
            )}

            {refManifest && (
              <RefManifest
                bucket={refManifest}
                onClose={() => setRefManifest(null)}
              />
            )}

            {bucketManifest && (
              <div className="ds-checkout-sec">
                <div className="ds-checkout-label">
                  {bucketManifest.container}
                  <CopyButton
                    get={() => bucketManifest.text}
                    label="copy"
                    done="copied"
                  />
                  <button className="ds-link" onClick={() => setBucketManifest(null)}>
                    hide
                  </button>
                </div>
                <pre className="ds-raw">{bucketManifest.text}</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- player ---------- */}
      {player && (
        <VideoPlayer
          state={player}
          onClose={() => setPlayer(null)}
          onMeta={() => {
            const fn = player.video.filename;
            setPlayer(null);
            openMeta(fn);
          }}
        />
      )}

      {/* ---------- metadata drawer ---------- */}
      {meta && (
        <div className="ds-drawer" onClick={() => setMeta(null)}>
          <div className="ds-drawer-inner" onClick={(e) => e.stopPropagation()}>
            <div className="ds-drawer-head">
              <b>{meta.filename}</b>
              <button onClick={() => setMeta(null)}>close</button>
            </div>
            <pre>{metaBusy ? 'probing…' : JSON.stringify(meta.json, null, 1)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Playback that opens on the search match.
 *
 * Modelled on plix-soc's AssetDrawer: the hits are markers, the player lands
 * PRE_START seconds ahead of one, and prev/next step between them. Without that
 * a hit in a 54-minute recording is a filename and a promise — you would have to
 * scrub for it.
 */
/* ------------------------------------------------------------------ *
 * Reference-bucket delivery manifest
 * ------------------------------------------------------------------ */

type RMKind = 'derived' | 'entered';
type RMVerdict = 'ok' | 'warn' | 'fail';
/** Where a value came from, worst-to-best confidence left to right:
 *  inferred (a model looked at frames) · geocoded (GPS, +/-40m) ·
 *  computed (deterministic) · measured (read off the bytes) · typed (a person). */
type RMSource = 'inferred' | 'geocoded' | 'computed' | 'measured' | 'typed';

interface RMField {
  key: string;
  label: string;
  kind: RMKind;
  required: boolean;
  spec: string;
  value: string | null;
  note: string | null;
  verdict: RMVerdict | null;
  source: RMSource | null;
  calculated: boolean;
  present: boolean;
}
interface RMSection {
  id: string;
  title: string;
  blurb: string;
  fields: RMField[];
}
interface RMDoc {
  id: string;
  name: string;
  bucket: string;
  prefix: string;
  sections: RMSection[];
  counts: {
    required: number;
    present: number;
    missing: number;
    calculated: number;
    confirmed: number;
  };
  visionMeta?: {
    confidence?: string;
    reasoning?: string;
    is_residential?: boolean;
    business_kind?: string | null;
    model?: string;
    frames?: number;
    at?: string;
    rejected?: string[];
  };
  shape?: {
    segments: number[];
    streams: Record<string, number>;
    videos: number;
    objects: number;
    bytes: number;
  };
  probe?: { videos: number | null; probed: number };
  errors: string[];
  truncated?: boolean;
  updatedAt?: string | null;
  storeError?: string | null;
}

/**
 * What one reference bucket can prove about itself, against the robotics data
 * requirements, plus the fields a person still has to supply.
 *
 * The two halves are not symmetric and the panel says so. Derived rows are
 * measured from the bucket every time this opens — object listing, the vendor's
 * transfer sidecar, and an MP4 container probe — and are read-only, because a
 * value you can type over a measurement is a value that will eventually
 * disagree with the data. Entered rows are the ones no file in the bucket
 * knows: business, operator, task, consent, calibration.
 *
 * A field counts as present when it holds anything at all. This is a checklist
 * for "what is still blank", not a validator.
 */
function RefManifest({
  bucket,
  onClose,
}: {
  bucket: ExternalBucket;
  onClose: () => void;
}) {
  const [doc, setDoc] = useState<RMDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  // Edits live here until saved. Keyed by field; a key present with '' means
  // "cleared", which the backend drops back to missing.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const { username } = useAuth();

  const load = useCallback(
    async (refresh = false) => {
      setBusy(true);
      setError(null);
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/wearer-hands/external/${bucket.id}/manifest` +
            (refresh ? '?refresh=1' : ''),
        );
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        setDoc(j);
        setDraft({});
      } catch (e: any) {
        setError(String(e.message || e));
      } finally {
        setBusy(false);
      }
    },
    [bucket.id],
  );

  useEffect(() => {
    load();
  }, [load]);

  const dirty = Object.keys(draft).length;

  /** Everything typed so far, merged over what is stored — the shape the PUT
   *  wants. Sent whole rather than as a patch so that clearing a field is
   *  expressible: an absent key would read as "leave it alone". */
  const save = async () => {
    if (!doc) return;
    const fields: Record<string, string> = {};
    doc.sections.forEach((s) =>
      s.fields.forEach((f) => {
        if (f.kind !== 'entered') return;
        const v = f.key in draft ? draft[f.key] : f.value || '';
        if (v.trim()) fields[f.key] = v.trim();
      }),
    );
    setSaving('saving');
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/wearer-hands/external/${bucket.id}/manifest`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          // `changed` is what the person actually touched. The backend flips
          // exactly those to `typed` and leaves every other field's provenance
          // alone, so saving one edit does not relabel six inferences as human
          // answers.
          body: JSON.stringify({
            fields,
            changed: Object.keys(draft),
            updatedBy: username || null,
          }),
        },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSaving('saved');
      setTimeout(() => setSaving('idle'), 1600);
      await load();
    } catch (e: any) {
      setError(String(e.message || e));
      setSaving('idle');
    }
  };

  const shown = (f: RMField) =>
    !onlyMissing || !(f.key in draft ? draft[f.key].trim() : f.present);

  return (
    <div className="ds-checkout-sec ds-rm">
      <div className="ds-checkout-label ds-rm-head">
        {bucket.name} — delivery manifest
        <span className="ds-rm-spacer" />
        <label className="ds-rm-toggle">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
          />
          only missing
        </label>
        {/* No inference control yet, on purpose. The backend route is live and
            so is its output -- fields already inferred still render, with their
            `inferred` chip -- but it costs an API call and its answer moved
            between runs, so it is not a button to hand out until those labels
            are trusted. To restore: POST
            /api/wearer-hands/external/<id>/manifest/enrich, then reload. */}
        <button
          className="ds-link"
          disabled={busy}
          title="Re-read the bucket and recompute every measured field. Does not re-run the inference, and does not touch anything you typed."
          onClick={() => load(true)}
        >
          {busy ? 'reading…' : 're-read'}
        </button>
        {/* An <a>, not a fetch-and-Blob: the route sets Content-Disposition, so
            the browser saves it with the right filename and nothing holds the
            file in memory. It needs its own class because the header label sets
            uppercase and letter-spacing, which an inherited link wears too. */}
        <a
          className="ds-rm-export"
          href={`${API_BASE_URL}/api/wearer-hands/external/${bucket.id}/manifest/export`}
          title="Download the manifest as JSON. Every field the requirements define is present; unfilled ones are null, and provenance and the missing list ride along."
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M8 2v8m0 0 3-3m-3 3-3-3M3 12.5h10" />
          </svg>
          export json
        </a>
        <button className="ds-link" onClick={onClose}>
          hide
        </button>
      </div>

      {error && <div className="ds-rm-err">{error}</div>}

      {doc && (
        <>
          {/* Two-tone deliberately. A single bar at 35/56 would read as "we are
              two thirds done", when almost none of that has been confirmed by a
              person — the hatched part is the difference between having a value
              and standing behind it. */}
          {/* Everything the two lines under here used to spell out is now the
              bar's tooltip: the solid/to-verify split is already the bar's two
              colours, and the object counts are provenance for a measurement
              nobody is disputing. */}
          <div
            className="ds-rm-progress"
            title={
              `${doc.counts.confirmed} solid · ${doc.counts.calculated} to verify · ` +
              `${doc.counts.missing} missing` +
              (doc.shape
                ? `\n${doc.shape.objects.toLocaleString()} objects · ` +
                  `${fmtBytes(doc.shape.bytes)} · ${doc.shape.segments.length} segments × ` +
                  `${Object.keys(doc.shape.streams).length} streams\n` +
                  `capture specs read from ${doc.probe?.probed ?? 0}/${doc.shape.videos} videos` +
                  (doc.visionMeta?.frames
                    ? `\nenvironment and task inferred from ${doc.visionMeta.frames} frames` +
                      (doc.visionMeta.confidence
                        ? `, ${doc.visionMeta.confidence} confidence`
                        : '')
                    : '')
                : '')
            }
          >
            <div className="ds-rm-bar">
              <span
                className="ds-rm-bar-ok"
                style={{ width: `${(doc.counts.confirmed / doc.counts.required) * 100}%` }}
              />
              <span
                className="ds-rm-bar-calc"
                style={{ width: `${(doc.counts.calculated / doc.counts.required) * 100}%` }}
              />
            </div>
            <b>
              {doc.counts.present} of {doc.counts.required}
            </b>
            <span className="ds-rm-sub">required</span>
            {/* The chip stays on the top line; the sentence that used to
                introduce it does not. What the model looked at and how sure it
                was is a hover, not a headline. */}
            {doc.visionMeta?.is_residential && (
              <span
                className="ds-rm-chip resi"
                title="Residential environments are out of scope unless approved in writing before collection — Task and Environment Diversity 7.1, FAQ Q7."
              >
                reads as residential
              </span>
            )}
            {doc.updatedAt && (
              <span className="ds-rm-sub ds-rm-when">
                edited {doc.updatedAt.slice(0, 16).replace('T', ' ')}
              </span>
            )}
          </div>

          {doc.storeError && (
            <div className="ds-rm-err">
              typed fields unavailable — {doc.storeError}
            </div>
          )}
          {doc.errors.map((e) => (
            <div className="ds-rm-warn" key={e}>
              {e}
            </div>
          ))}

          {doc.sections.map((sec) => {
            const fields = sec.fields.filter(shown);
            if (!fields.length) return null;
            const miss = sec.fields.filter((f) => f.required && !f.present).length;
            return (
              <div className="ds-rm-sec" key={sec.id}>
                {/* The section blurb is the spec's provenance ("Requirements
                    section 5.1, carried on session-metadata.*"). True, but it
                    repeats on every header and nobody reads it twice — it
                    moved to the title. */}
                <div className="ds-rm-sectitle" title={sec.blurb}>
                  {sec.title}
                  {miss > 0 && <span className="ds-rm-secmiss">{miss} missing</span>}
                </div>
                <div className="ds-rm-grid">
                  {fields.map((f) => {
                    const v = f.key in draft ? draft[f.key] : f.value || '';
                    const has = !!v.trim();
                    return (
                      <React.Fragment key={f.key}>
                        <div
                          className={`ds-rm-label${has ? '' : ' ds-rm-lblmiss'}`}
                          title={f.spec}
                        >
                          <i
                            className={`ds-rm-dot${
                              has ? (f.calculated && !(f.key in draft) ? ' calc' : ' on') : ''
                            }`}
                          />
                          {f.label}
                          {!f.required && <em> optional</em>}
                        </div>
                        <div className="ds-rm-val">
                          {f.kind === 'derived' ? (
                            <span className="ds-rm-measured">
                              {f.value || <em className="ds-rm-none">not measurable here</em>}
                              {f.verdict && (
                                <span className={`ds-rm-verdict v-${f.verdict}`}>
                                  {f.verdict}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="ds-rm-inputrow">
                              <input
                                className={`ds-rm-input${
                                  f.key in draft ? ' edited' : ''
                                }`}
                                value={v}
                                placeholder="—"
                                onChange={(e) =>
                                  setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                                }
                              />
                              {/* Only worth a chip when the value did not come
                                  from a person — 'typed' is the default reading
                                  of a filled-in box and labelling it adds
                                  nothing. */}
                              {has && !(f.key in draft) && f.calculated && f.source && (
                                <span className={`ds-rm-src s-${f.source}`}>{f.source}</span>
                              )}
                            </span>
                          )}
                          {f.note && <div className="ds-rm-note">{f.note}</div>}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {(dirty > 0 || saving !== 'idle') && (
            <div className="ds-rm-savebar">
              <span>
                {saving === 'saved'
                  ? 'saved'
                  : `${dirty} unsaved ${dirty === 1 ? 'change' : 'changes'}`}
              </span>
              <button className="ds-link" onClick={() => setDraft({})}>
                discard
              </button>
              <button
                className="ds-rm-save"
                disabled={saving === 'saving' || !dirty}
                onClick={save}
              >
                {saving === 'saving' ? 'saving…' : 'save'}
              </button>
            </div>
          )}
        </>
      )}
      {busy && !doc && <div className="ds-rm-note">measuring the bucket…</div>}
    </div>
  );
}

function VideoPlayer({
  state,
  onClose,
  onMeta,
}: {
  state: { video: WHVideo; url: string; marks: Marker[]; at: number };
  onClose: () => void;
  onMeta: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [idx, setIdx] = useState(0);
  const { video, url, marks } = state;

  const jump = useCallback(
    (i: number) => {
      const m = marks[i];
      if (!m || !ref.current) return;
      setIdx(i);
      ref.current.currentTime = Math.max(0, m.t - PRE_START);
      ref.current.play().catch(() => {
        /* autoplay may be blocked; the seek still lands */
      });
    },
    [marks],
  );

  // Esc closes, arrows step between matches — this gets used with one hand while
  // reading, so it should not require the mouse.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && idx < marks.length - 1) jump(idx + 1);
      if (e.key === 'ArrowLeft' && idx > 0) jump(idx - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, marks.length, jump, onClose]);

  const mmss = (t: number) =>
    `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

  return (
    <div className="ds-modal" onClick={onClose}>
      <div className="ds-player" onClick={(e) => e.stopPropagation()}>
        <div className="ds-player-head">
          <b title={video.filename}>{video.filename}</b>
          <span className="ds-player-meta">
            {video.orgName} · {video.industry} · {mmss(video.duration)}
          </span>
          <button onClick={onMeta}>metadata</button>
          <button onClick={onClose}>close</button>
        </div>

        <video
          ref={ref}
          src={url}
          controls
          autoPlay
          className="ds-video"
          onLoadedMetadata={(e) => {
            (e.target as HTMLVideoElement).currentTime = state.at;
          }}
        />

        {/* No markers means no search is running, so there is nothing to step
            through and nothing worth saying about it. */}
        {marks.length > 0 && (
          <>
            {/* Matches as ticks along the recording, so you can see whether the
                search found one moment or a dozen spread through a shift. */}
            <div className="ds-track">
              {marks.map((m, i) => (
                <button
                  key={`${m.t}-${i}`}
                  className={`ds-tick-mark${i === idx ? ' on' : ''}`}
                  style={{ left: `${(m.t / Math.max(1, video.duration)) * 100}%` }}
                  title={`${mmss(m.t)} — ${m.label}`}
                  onClick={() => jump(i)}
                />
              ))}
            </div>
            <div className="ds-player-foot">
              <button disabled={idx <= 0} onClick={() => jump(idx - 1)}>
                ← prev
              </button>
              <span className="ds-player-n">
                match {idx + 1} of {marks.length} · {mmss(marks[idx].t)}
              </span>
              <button
                disabled={idx >= marks.length - 1}
                onClick={() => jump(idx + 1)}
              >
                next →
              </button>
              <span className="ds-player-label" title={marks[idx].label}>
                {marks[idx].label}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Copy with confirmation.
 *
 * Clipboard writes are silent — nothing about the page changes — so a plain
 * "copy link" leaves you unsure whether it worked, and people click it twice.
 * `get` is async because some of these mint a fresh SAS before copying.
 */
/** The cart's org + category mix, for a bucket that has already shipped.
 *
 * Reuses the cart's `ds-orgmix` markup so a delivery looks the same after the
 * fact as it did in the cart — the numbers are the point, and a second visual
 * language for them would only invite the question of whether they mean the
 * same thing. They do; they are just computed from the shipped rows instead of
 * the live selection. */
function BucketMix({ breakdown }: { breakdown: BucketBreakdown }) {
  const hours = breakdown.seconds / 3600;
  const rows = (
    items: { key: string; label: string; pct: number; seconds: number; videos: number }[],
  ) => (
    <div className="ds-orgmix-list">
      {items.map((it) => (
        <div className="ds-orgmix-row" key={it.key}>
          <span className="ds-orgmix-name" title={it.key}>
            {it.label}
          </span>
          <span className="ds-orgmix-bar">
            <i style={{ width: `${Math.min(Math.max(it.pct, 0.6), 100)}%` }} />
          </span>
          <span className="ds-orgmix-pct">
            {it.pct >= 1 ? it.pct.toFixed(0) : it.pct.toFixed(1)}%
          </span>
          <span className="ds-orgmix-amt">{fmtHours(it.seconds / 3600)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="ds-bucket-mix">
      <div className="ds-orgmix">
        <div className="ds-orgmix-head">
          share of delivered footage · {fmtHours(hours)} across{' '}
          {breakdown.orgs.length}{' '}
          {breakdown.orgs.length === 1 ? 'organization' : 'organizations'}
        </div>
        {rows(
          breakdown.orgs.map((o) => ({
            key: o.orgId,
            label: o.orgName,
            pct: o.pct,
            seconds: o.seconds,
            videos: o.videos,
          })),
        )}
      </div>
      <div className="ds-orgmix">
        <div className="ds-orgmix-head">
          share of delivered footage · {fmtHours(hours)} across{' '}
          {breakdown.categories.length}{' '}
          {breakdown.categories.length === 1 ? 'category' : 'categories'}
        </div>
        {rows(
          breakdown.categories.map((c) => ({
            key: c.term,
            label: c.term,
            pct: c.pct,
            seconds: c.seconds,
            videos: c.videos,
          })),
        )}
      </div>
      {/* Where it was shot. Only for buckets delivered since this was recorded;
          an older one can gain it by reconciling. */}
      {!!breakdown.environments?.length && (
        <div className="ds-orgmix">
          <div className="ds-orgmix-head">
            share of delivered footage · {fmtHours(hours)} across{' '}
            {breakdown.environments.length}{' '}
            {breakdown.environments.length === 1
              ? 'environment'
              : 'environments'}
          </div>
          {rows(
            breakdown.environments.map((e) => ({
              key: e.environment,
              label: e.environment,
              pct: e.pct,
              seconds: e.seconds,
              videos: e.videos,
            })),
          )}
        </div>
      )}
    </div>
  );
}

/** A video count with the link to the container holding it underneath.
 *
 *  Two earlier cuts failed for the same reason, and the reason is worth keeping:
 *  the count and the link are DIFFERENT THINGS, and every attempt to fuse them
 *  into one control made the control unreadable. A bordered pill holding a
 *  number is the shape of a status badge, so it read as one; adding an icon and
 *  a hover-revealed `copy` said an action existed but never said what it acted
 *  on. So they are separate now: the number is data, and below it sits a button
 *  that names its own job in full.
 *
 *  It stacks rather than sitting inline because the Bucket cell is already two
 *  lines tall — the label is free vertically, where inline it would have cost
 *  ~120px of width per column.
 *
 *  `original` and `redacted` are spelled out rather than left to the column
 *  header or the hue, because handing a lab un-blurred footage is the expensive
 *  mistake here and nothing about it should require inference. */
function LinkPill({
  kind,
  count,
  get,
  title,
}: {
  kind: 'src' | 'red';
  count: number;
  get: () => string | Promise<string>;
  title: string;
}) {
  return (
    <span className="ds-pillstack">
      <span className="ds-pillcount">{count.toLocaleString()}</span>
      <CopyButton
        className={`ds-pill ds-pill-${kind}`}
        get={get}
        title={title}
        done="link copied ✓"
        label={
          <>
            <svg
              className="ds-pillicon"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6.6 9.4a3 3 0 0 0 4.24 0l2.12-2.12a3 3 0 0 0-4.24-4.24l-1 1" />
              <path d="M9.4 6.6a3 3 0 0 0-4.24 0L3.04 8.72a3 3 0 0 0 4.24 4.24l1-1" />
            </svg>
            copy {kind === 'red' ? 'redacted' : 'original'} link
          </>
        }
      />
    </span>
  );
}

function CopyButton({
  get,
  label = 'copy link',
  done = 'link copied',
  className = 'ds-link',
  title,
}: {
  get: () => string | Promise<string>;
  /** A node, not just a string: the count pills put an icon and a hover hint
   *  inside the button, which is what makes them read as clickable. */
  label?: React.ReactNode;
  done?: string;
  className?: string;
  /** Spells out WHICH link this copies. Two copy buttons on one row otherwise
   *  look interchangeable, and the redacted one is not. */
  title?: string;
}) {
  const [state, setState] = useState<'idle' | 'ok' | 'err'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const click = async () => {
    try {
      await navigator.clipboard.writeText(await get());
      setState('ok');
    } catch {
      // Says so rather than looking like it worked: the clipboard API refuses
      // outright on an insecure origin or a denied permission.
      setState('err');
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 1600);
  };

  return (
    <button
      type="button"
      className={`${className}${state === 'ok' ? ' ds-copied' : ''}${
        state === 'err' ? ' ds-copyfail' : ''
      }`}
      title={title}
      onClick={click}
      disabled={state !== 'idle'}
    >
      {state === 'ok' ? done : state === 'err' ? 'copy failed' : label}
    </button>
  );
}
